// MedicalStuffDashboard.jsx — REST-only live vitals (no MQTT) — MOBILE RESPONSIVE
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import emailjs from "@emailjs/browser";

import { API } from "../config";

const EMAILJS_SERVICE  = "service_2102gkp";
const EMAILJS_TEMPLATE = "template_a3msbeq";
const EMAILJS_KEY      = "S-V2yY6sriTrukATB";

const THREAT_KEYWORDS_CLIENT = [
  "critical","urgent","emergency","danger","abnormal","severe",
  "irregular","alarming","threat","anormal","critique","urgence",
  "dangereux","grave","cardiac","hypoxia","tachycardia","bradycardia",
  "fever","hyperthermia","hypothermia","icu","hospitalization",
  "hospitalisé","réanimation","arrhythmia","dehydration","low oxygen",
  "unconscious","unresponsive","seizure","stroke","collapse","syncope",
  "chest pain","dyspnea","apnea","cyanosis","hemorrhage","infection",
  "sepsis","shock","hypertension","hypotension","hyperglycemia","hypoglycemia",
];

// ── Vital config ───────────────────────────────────────────────
const VC = {
  hr:          { label: "Heart Rate",   unit: "bpm", icon: "favorite",          color: "#ef4444", bg: "#fef2f2", normal: [60,100],    warn: [50,130]  },
  spo2:        { label: "SpO₂",         unit: "%",   icon: "air",               color: "#3b82f6", bg: "#eff6ff", normal: [95,100],    warn: [90,100]  },
  temp_body:   { label: "Body Temp",    unit: "°C",  icon: "thermostat",        color: "#f59e0b", bg: "#fffbeb", normal: [36.1,37.2], warn: [35,38.5] },
  humidity:    { label: "Humidity",     unit: "%",   icon: "water_drop",        color: "#06b6d4", bg: "#ecfeff", normal: [30,60],     warn: [20,80]   },
  air_quality: { label: "Air Quality",  unit: "ppm", icon: "air_purifier_gen",  color: "#10b981", bg: "#f0fdf4", normal: [400,1000],  warn: [1000,2000] },
};

const vStat = (col, val) => {
  const c = VC[col];
  if (!c || val == null) return "unknown";
  const v = +val;
  if (col === "air_quality") {
    if (v >= c.normal[0]) return "Normal";
    if (v >= c.warn[0])   return "Watch";
    return "Abnormal";
  }
  if (v >= c.normal[0] && v <= c.normal[1]) return "Normal";
  if (v >= c.warn[0]   && v <= c.warn[1])   return "Watch";
  return "Abnormal";
};

const SS = {
  Normal:   { bg: "#f0fdf4", c: "#166534", dot: "#22c55e", border: "#bbf7d0" },
  Watch:    { bg: "#fffbeb", c: "#b45309", dot: "#f59e0b", border: "#fde68a" },
  Abnormal: { bg: "#fef2f2", c: "#991b1b", dot: "#ef4444", border: "#fecaca" },
  unknown:  { bg: "#f3f4f6", c: "#6b7280", dot: "#9ca3af", border: "#e5e7eb" },
};

const AS = {
  new:     { bg: "#fef2f2", c: "#991b1b", label: "New"     },
  seen:    { bg: "#fffbeb", c: "#b45309", label: "Seen"    },
  treated: { bg: "#f0fdf4", c: "#166534", label: "Treated" },
};

const fmt  = d => d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const fmtT = d => d ? new Date(d).toLocaleString("en-GB",    { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
const fmtN = v => v != null ? (+v).toFixed(1) : "—";

const timeAgo = dateStr => {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60)    return `${diff}s ago`;
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const COLORS = ["#3b82f6","#8b5cf6","#06b6d4","#10b981","#f59e0b","#ef4444","#ec4899"];
const pickColor = name => COLORS[(name||"").charCodeAt(0) % COLORS.length];

const getAirQualityLabel = v => {
  if (v == null) return "—";
  const n = +v;
  // CO2 en ppm (SCD40) — seuils OMS / ASHRAE
  if (n <= 600)  return "Excellent";
  if (n <= 800)  return "Good";
  if (n <= 1000) return "Moderate";
  if (n <= 2000) return "Poor";
  return "Dangerous";
};

// ── Mobile-aware global styles ──────────────────────────────────────────
const MOBILE_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
  @import url('https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@24,400,0,0&display=swap');

  .ms{font-family:'Material Symbols Outlined';font-style:normal;font-weight:normal;line-height:1;display:inline-block;white-space:nowrap;direction:ltr;-webkit-font-smoothing:antialiased;}
  ::-webkit-scrollbar{width:5px;height:5px}::-webkit-scrollbar-track{background:transparent}::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:20px}
  .card{background:#fff;border-radius:1.5rem;box-shadow:0 4px 20px -2px rgba(0,0,0,.06);transition:all .3s;}
  .pill{border:none;cursor:pointer;font-family:inherit;font-size:.8125rem;font-weight:600;border-radius:9999px;padding:.5rem 1.1rem;display:inline-flex;align-items:center;gap:.375rem;transition:all .15s;white-space:nowrap;}
  .pact{background:#3b82f6!important;color:#fff!important;box-shadow:0 4px 14px rgba(59,130,246,.35)!important;}
  .pinact{background:transparent;color:#6b7280;}.pinact:hover{background:#f3f4f6;color:#111827;}
  .inp{border:1.5px solid #e5e7eb;border-radius:.875rem;padding:.75rem 1rem;font-size:.875rem;outline:none;width:100%;box-sizing:border-box;font-family:inherit;transition:border-color .15s;}
  .inp:focus{border-color:#93c5fd;box-shadow:0 0 0 3px rgba(59,130,246,.1);}
  .bblu{background:#3b82f6;color:#fff;border:none;font-family:inherit;font-weight:700;border-radius:.875rem;padding:.75rem 1.5rem;cursor:pointer;transition:background .15s;font-size:.9375rem;display:inline-flex;align-items:center;gap:.5rem;}
  .bblu:hover{background:#2563eb;}.bblu:disabled{background:#93c5fd;cursor:not-allowed;}
  .bgry{background:#f3f4f6;color:#374151;border:none;font-family:inherit;font-weight:600;border-radius:.875rem;padding:.75rem 1.5rem;cursor:pointer;transition:background .15s;font-size:.9375rem;}
  .bgry:hover{background:#e5e7eb;}
  .trow:hover{background:#f9fafb;}
  .hovcard{transition:box-shadow .2s;}.hovcard:hover{box-shadow:0 8px 28px -4px rgba(0,0,0,.12);}
  .calday{width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:.875rem;font-weight:600;margin:auto;transition:all .15s;}
  .cdused{background:#3b82f6;color:#fff;box-shadow:0 2px 8px rgba(59,130,246,.4);}
  .cdmiss{background:#fef2f2;color:#ef4444;}
  .cdfut{color:#d1d5db;cursor:default;}
  .calday:not(.cdused):not(.cdmiss):not(.cdfut):hover{background:#eff6ff;color:#3b82f6;}
  @keyframes spin{to{transform:rotate(360deg)}}
  @keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}
  @keyframes bounce{0%,100%{transform:translateY(0)}50%{transform:translateY(-4px)}}
  @keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
  @keyframes slideUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
  .spin{animation:spin 1s linear infinite}
  .pls{animation:pulse 2s infinite}
  .fade-in{animation:fadeIn .25s ease both}
  .slide-up{animation:slideUp .3s ease both}
  select,textarea,input{font-family:'Plus Jakarta Sans',sans-serif;}
  .treat-btn{background:#f0fdf4;color:#166534;border:1px solid #bbf7d0;border-radius:.875rem;font-weight:700;font-size:.8125rem;cursor:pointer;font-family:inherit;display:inline-flex;align-items:center;gap:.3rem;padding:.5rem 1rem;transition:all .15s;}
  .treat-btn:hover{background:#dcfce7;border-color:#86efac;box-shadow:0 2px 10px rgba(34,197,94,.2);}
  .treat-btn-urgent{background:#22c55e;color:#fff;border:none;animation:treatPulse 2s infinite;}
  @keyframes treatPulse{0%,100%{box-shadow:0 0 0 0 rgba(34,197,94,.4)}50%{box-shadow:0 0 0 6px rgba(34,197,94,0)}}

  /* ── Modal — desktop: centered, mobile: sheet from bottom ── */
  .modal-overlay{
    position:fixed;inset:0;
    background:rgba(0,0,0,.45);
    backdrop-filter:blur(4px);
    display:flex;
    align-items:center;
    justify-content:center;
    z-index:60;
    padding:1rem;
  }
  .modal-box{
    max-height:90dvh;
    overflow-y:auto;
    -webkit-overflow-scrolling:touch;
  }

  /* Desktop defaults */
  .msd-mobile-header { display: none; }
  .msd-bottom-nav    { display: none; }
  .msd-desktop-nav   { display: flex; }
  .msd-main-pad      { padding: 1.5rem 2rem; }
  .msd-page-content  { max-width: 1600px; margin: 0 auto; }

  /* Mobile bottom nav item */
  .mob-nav-item{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:2px;flex:1;padding:.5rem .25rem;cursor:pointer;border:none;background:none;font-family:inherit;font-size:.5rem;font-weight:700;color:#9ca3af;transition:color .15s;position:relative;}
  .mob-nav-item.active{color:#3b82f6;}
  .mob-nav-item.active::before{content:'';position:absolute;top:0;left:50%;transform:translateX(-50%);width:28px;height:3px;background:#3b82f6;border-radius:0 0 4px 4px;}

  /* Notify patient button */
  .notif-send-btn{
    margin-top:.625rem;width:100%;
    padding:.5rem .875rem;
    background:linear-gradient(135deg,#22c55e,#16a34a);
    color:#fff;border:none;border-radius:.875rem;
    font-weight:700;font-size:.75rem;cursor:pointer;
    font-family:inherit;display:flex;align-items:center;
    justify-content:center;gap:.375rem;
    box-shadow:0 3px 10px rgba(34,197,94,.3);
    transition:opacity .15s;
  }
  .notif-send-btn:hover{opacity:.9;}

  @media (max-width: 768px) {
    .msd-mobile-header { display: flex; align-items: center; justify-content: space-between; padding: .875rem 1rem; background: #fff; border-bottom: 1px solid #f0f0f0; position: sticky; top: 0; z-index: 40; }
    .msd-bottom-nav { display: flex; position: fixed; bottom: 0; left: 0; right: 0; background: #fff; border-top: 1px solid #f0f0f0; z-index: 40; padding-bottom: env(safe-area-inset-bottom, 0); overflow-x: auto; }
    .msd-desktop-nav { display: none !important; }
    .msd-desktop-header { display: none !important; }
    .msd-main-pad { padding: 1rem; padding-bottom: 80px; }

    /* Grid overrides */
    .ov-two-col    { grid-template-columns: 1fr !important; }
    .ov-stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
    .obs-grid      { grid-template-columns: 1fr !important; }
    .cal-grid      { grid-template-columns: 1fr !important; }
    .vit-grid      { grid-template-columns: repeat(2, 1fr) !important; }
    .ai-grid       { grid-template-columns: 1fr !important; }
    .ai-sidebar    { display: none !important; }

    /* Chat */
    .chat-desktop-layout { display: none !important; }
    .chat-mobile-layout  { display: flex !important; }

    /* Modal — slide up from bottom on mobile */
    .modal-overlay {
      align-items: flex-end !important;
      padding: 0 !important;
    }
    .modal-box {
      border-radius: 1.5rem 1.5rem 0 0 !important;
      max-height: 92dvh;
    }

    /* Typography scale-down */
    h1 { font-size: 1.25rem !important; }

    /* Live devices */
    .live-devices-wrap { flex-direction: column; }
  }
`;

// ── Sparkline ──
const Spark = ({ data = [], color = "#3b82f6", w = 120, h = 40, animate = true }) => {
  const vals = data.map(Number).filter(v => !isNaN(v));
  if (vals.length < 2) return <svg width={w} height={h} />;
  const mn  = Math.min(...vals);
  const mx  = Math.max(...vals);
  const rng = mx - mn || 1;
  const pts = vals
    .map((v, i) => `${(i / (vals.length - 1)) * w},${h - ((v - mn) / rng) * (h - 6) - 3}`)
    .join(" ");
  const gid = `sg${color.replace(/[^a-z0-9]/gi, "")}`;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} overflow="visible">
      <defs>
        <linearGradient id={gid} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%"   stopColor={color} stopOpacity=".2" />
          <stop offset="100%" stopColor={color} stopOpacity="0"  />
        </linearGradient>
      </defs>
      <polygon points={`0,${h} ${pts} ${w},${h}`} fill={`url(#${gid})`} />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {animate && (
          <animate attributeName="stroke-dasharray" values={`0,${pts.length * 2};${pts.length * 2},0`} dur="1s" fill="freeze" />
        )}
      </polyline>
      {(() => {
        const [x, y] = pts.split(" ").pop().split(",");
        return <circle cx={x} cy={y} r="3" fill={color} />;
      })()}
    </svg>
  );
};

// ── Semicircle gauge ──
const Gauge = ({ value, min, max, color }) => {
  const pct = Math.min(1, Math.max(0, (+value - min) / (max - min)));
  const r = 36, cx = 44, cy = 44;
  const xy = deg => {
    const rad = ((deg - 90) * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  };
  const s = xy(-90), e = xy(pct * 180 - 90);
  const large = pct * 180 > 180 ? 1 : 0;
  return (
    <svg width="88" height="52" viewBox="0 0 88 52">
      <path d={`M${xy(-90).x} ${xy(-90).y} A${r} ${r} 0 0 1 ${xy(90).x} ${xy(90).y}`}
        fill="none" stroke="#f3f4f6" strokeWidth="8" strokeLinecap="round" />
      {pct > 0 && (
        <path d={`M${s.x} ${s.y} A${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`}
          fill="none" stroke={color} strokeWidth="8" strokeLinecap="round" />
      )}
    </svg>
  );
};

// ── Histogram ──
const RANGES = [
  { key: "hours_day",   label: "Hours / Day"   },
  { key: "days_week",   label: "Days / Week"   },
  { key: "days_month",  label: "Days / Month"  },
  { key: "weeks_month", label: "Weeks / Month" },
];
const HIST_METRICS = [
  { key: "hr",          label: "Heart Rate",  unit: "bpm", color: "#ef4444" },
  { key: "spo2",        label: "SpO₂",        unit: "%",   color: "#3b82f6" },
  { key: "temp_body",   label: "Body Temp",   unit: "°C",  color: "#f59e0b" },
  { key: "humidity",    label: "Humidity",    unit: "%",   color: "#06b6d4" },
  { key: "air_quality", label: "Air Quality", unit: "ppm", color: "#10b981" },
];

function niceScale(min, max, tickCount = 5) {
  const range     = max - min || 1;
  const roughStep = range / (tickCount - 1);
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const niceSteps = [1, 2, 2.5, 5, 10];
  const step      = niceSteps.find(s => s * magnitude >= roughStep) * magnitude;
  const niceMin   = Math.floor(min / step) * step;
  const niceMax   = Math.ceil(max  / step) * step;
  const ticks     = [];
  for (let t = niceMin; t <= niceMax + step * 0.01; t = +(t + step).toFixed(10))
    ticks.push(+t.toFixed(4));
  return { min: niceMin, max: niceMax, ticks };
}

function formatBucket(bucket, range) {
  if (!bucket) return "";
  const s = String(bucket);
  if (range === "hours_day") { const m = s.match(/(\d{2}:\d{2})/); return m ? m[1] : s; }
  if (range === "weeks_month") return "Wk " + s.slice(4);
  const d = new Date(s);
  return isNaN(d) ? s : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const Histogram = ({ userId, patientId }) => {
  const [range,   setRange]   = useState("hours_day");
  const [metric,  setMetric]  = useState("hr");
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchHist = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/measurements-histogram?userId=${userId}&range=${range}`;
      if (patientId) url += `&patientId=${patientId}`;
      const r = await fetch(url);
      const d = await r.json();
      setData(Array.isArray(d) ? d : []);
    } catch { setData([]); }
    setLoading(false);
  }, [userId, patientId, range]);

  useEffect(() => { fetchHist(); }, [fetchHist]);

  const cfg   = HIST_METRICS.find(m => m.key === metric) || HIST_METRICS[0];
  const vals  = data.map(d => +(+d[cfg.key]).toFixed(2)).filter(v => !isNaN(v) && v > 0);
  const scale = niceScale(vals.length ? Math.min(...vals) : 0, vals.length ? Math.max(...vals) : 1, 5);
  const { min: yMin, max: yMax, ticks: yTicks } = scale;
  const yRange = yMax - yMin || 1;
  const CHART_H = 200, Y_AXIS_W = 48;

  return (
    <div className="card" style={{ padding: "1.5rem", marginTop: "1.5rem" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: ".75rem", marginBottom: "1.25rem" }}>
        <div>
          <h3 style={{ fontWeight: 700, fontSize: "1rem", color: "#111827", margin: 0 }}>Vital History</h3>
          <p style={{ fontSize: ".75rem", color: "#9ca3af", margin: ".2rem 0 0" }}>Averaged measurements by time range</p>
        </div>
        <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap" }}>
          {RANGES.map(r => (
            <button key={r.key} onClick={() => setRange(r.key)}
              style={{ padding: ".35rem .85rem", background: range === r.key ? "#3b82f6" : "#f3f4f6", color: range === r.key ? "#fff" : "#6b7280", border: "none", borderRadius: "9999px", fontSize: ".75rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", transition: "all .15s" }}>
              {r.label}
            </button>
          ))}
        </div>
      </div>
      <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap", marginBottom: "1.25rem" }}>
        {HIST_METRICS.map(m => (
          <button key={m.key} onClick={() => setMetric(m.key)}
            style={{ padding: ".3rem .8rem", background: metric === m.key ? m.color : "#f9fafb", color: metric === m.key ? "#fff" : "#6b7280", border: metric === m.key ? "none" : "1px solid #e5e7eb", borderRadius: "9999px", fontSize: ".75rem", fontWeight: 600, cursor: "pointer", fontFamily: "inherit", transition: "all .15s", display: "flex", alignItems: "center", gap: ".3rem" }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: metric === m.key ? "#fff" : m.color, display: "inline-block" }} />
            {m.label}
          </button>
        ))}
      </div>
      {loading ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: CHART_H + 40, gap: ".75rem" }}>
          <div className="spin" style={{ width: 28, height: 28, border: "3px solid #dbeafe", borderTopColor: "#3b82f6", borderRadius: "50%" }} />
          <span style={{ color: "#9ca3af", fontSize: ".875rem" }}>Loading…</span>
        </div>
      ) : data.length === 0 ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: CHART_H + 40, gap: ".5rem" }}>
          <span className="ms" style={{ fontSize: "2rem", color: "#e5e7eb" }}>bar_chart</span>
          <p style={{ color: "#9ca3af", fontSize: ".875rem", margin: 0 }}>No data for this range</p>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 0 }}>
          <div style={{ width: Y_AXIS_W, flexShrink: 0, position: "relative", height: CHART_H, marginBottom: 24 }}>
            {yTicks.map((tick, i) => (
              <div key={i} style={{ position: "absolute", left: 0, bottom: ((tick - yMin) / yRange) * CHART_H - 8, width: "100%", textAlign: "right", paddingRight: 8, fontSize: ".65rem", color: "#9ca3af", fontWeight: 600, whiteSpace: "nowrap", lineHeight: 1 }}>
                {Number.isInteger(tick) ? tick : tick.toFixed(1)}
              </div>
            ))}
          </div>
          <div style={{ flex: 1, overflowX: "auto" }}>
            <div style={{ minWidth: Math.max(data.length * 44, 300), height: CHART_H + 24, position: "relative" }}>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: CHART_H, pointerEvents: "none" }}>
                {yTicks.map((tick, i) => (
                  <div key={i} style={{ position: "absolute", left: 0, right: 0, bottom: ((tick - yMin) / yRange) * CHART_H, borderTop: tick === yMin ? "1.5px solid #e5e7eb" : "1px dashed #f0f0f0", zIndex: 0 }} />
                ))}
              </div>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: CHART_H, display: "flex", alignItems: "flex-end", gap: 3, padding: "0 2px", zIndex: 1 }}>
                {data.map((row, i) => {
                  const rawVal    = +(+row[cfg.key]).toFixed(2);
                  const isValid   = !isNaN(rawVal) && rawVal > 0;
                  const heightPct = isValid ? Math.max(((rawVal - yMin) / yRange) * 100, 1) : 0;
                  return (
                    <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", height: "100%", justifyContent: "flex-end" }}>
                      {isValid && (
                        <span style={{ fontSize: ".58rem", fontWeight: 700, color: cfg.color, whiteSpace: "nowrap", marginBottom: 2, opacity: heightPct < 15 ? 0 : 1 }}>
                          {rawVal}
                        </span>
                      )}
                      <div style={{ width: "100%", height: isValid ? `${heightPct}%` : "2px", background: isValid ? `linear-gradient(180deg, ${cfg.color} 0%, ${cfg.color}99 100%)` : "#f3f4f6", borderRadius: "4px 4px 2px 2px", transition: "height .4s cubic-bezier(.4,0,.2,1)", cursor: "default", position: "relative", minHeight: isValid ? 4 : 2 }}
                        title={isValid ? `${formatBucket(row.bucket, range)}: ${rawVal} ${cfg.unit}` : "No data"} />
                    </div>
                  );
                })}
              </div>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 24, display: "flex", alignItems: "center", gap: 3, padding: "0 2px" }}>
                {data.map((row, i) => (
                  <div key={i} style={{ flex: 1, textAlign: "center", fontSize: ".58rem", color: "#9ca3af", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {formatBucket(row.bucket, range)}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {vals.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: ".875rem", flexWrap: "wrap", gap: "1rem" }}>
          <span style={{ fontSize: ".7rem", color: "#9ca3af", fontStyle: "italic" }}>Unit: {cfg.unit}</span>
          <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
            {[
              { l: "Min",    v: Math.min(...vals).toFixed(1) },
              { l: "Max",    v: Math.max(...vals).toFixed(1) },
              { l: "Avg",    v: (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) },
              { l: "Points", v: vals.length },
            ].map(s => (
              <div key={s.l} style={{ textAlign: "center" }}>
                <p style={{ fontSize: ".9375rem", fontWeight: 700, color: "#111827", margin: 0 }}>
                  {s.v} <span style={{ fontSize: ".65rem", color: "#9ca3af", fontWeight: 400 }}>{s.l !== "Points" ? cfg.unit : ""}</span>
                </p>
                <p style={{ fontSize: ".65rem", color: "#9ca3af", margin: 0 }}>{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── ESP32 REST polling hook ───────────────────────────────────
function useEsp32Vitals(patientIds = []) {
  const [vitals,    setVitals]    = useState({});
  const [newAlerts, setNewAlerts] = useState({});
  const timerRef = useRef(null);

  const clearAlerts = useCallback((pid) => {
    setNewAlerts(prev => { const n = {...prev}; delete n[pid]; return n; });
  }, []);

  const poll = useCallback(async () => {
    for (const pid of patientIds) {
      try {
        const r = await fetch(`${API}/esp32/patient/${pid}`);
        if (!r.ok) continue;
        const data = await r.json();
        const live = data.latest ?? data;
        if (!live) continue;
        setVitals(prev => ({ ...prev, [pid]: { ...live, timestamp: live.created_at || new Date().toISOString() } }));
        const triggered = [];
        const checks = [
          { key: "hr",          label: "Heart Rate",  unit: "bpm", min: 50,  max: 130   },
          { key: "spo2",        label: "SpO₂",        unit: "%",   min: 90,  max: null  },
          { key: "temp_body",   label: "Body Temp",   unit: "°C",  min: 35,  max: 38.5  },
          { key: "air_quality", label: "Air Quality", unit: "ppm", min: null, max: 2000  },
        ];
        for (const c of checks) {
          const v = live[c.key];
          if (v == null) continue;
          if (v < c.min || (c.max != null && v > c.max)) {
            triggered.push({ label: c.label, value: (+v).toFixed(c.key === "air_quality" ? 3 : 1), unit: c.unit, min: c.min, max: c.max });
          }
        }
        if (triggered.length) setNewAlerts(prev => ({ ...prev, [pid]: triggered }));
      } catch { /* ignore */ }
    }
  }, [patientIds.join(",")]); // eslint-disable-line

  useEffect(() => {
    if (!patientIds.length) return;
    poll();
    timerRef.current = setInterval(poll, 3000);
    return () => clearInterval(timerRef.current);
  }, [poll]);

  return { vitals, newAlerts, clearAlerts };
}

// ── Live Devices Panel ──
const LiveDevices = ({ devices, patients, selectedPatientId, onSelectPatient }) => {
  if (devices.length === 0) return null;
  const getPatientForDevice = device => {
    if (device.patient_id) return patients.find(p => String(p.patient_id) === String(device.patient_id));
    return null;
  };
  return (
    <div style={{ background: "linear-gradient(135deg,#eff6ff 0%,#f8faff 60%)", border: "1px solid #bfdbfe", borderRadius: "1.25rem", padding: "1rem 1.25rem", marginBottom: "1.25rem" }}>
      <div style={{ display: "flex", alignItems: "center", gap: ".5rem", marginBottom: ".875rem" }}>
        <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#22c55e", boxShadow: "0 0 6px #22c55e" }} className="pls" />
        <h3 style={{ fontWeight: 700, fontSize: ".875rem", color: "#1e40af", margin: 0 }}>Live Devices</h3>
        <span style={{ background: "#dbeafe", color: "#1d4ed8", fontSize: ".65rem", fontWeight: 700, padding: "1px 7px", borderRadius: "9999px", marginLeft: "auto" }}>{devices.length} connected</span>
      </div>
      <div className="live-devices-wrap" style={{ display: "flex", flexWrap: "wrap", gap: ".5rem" }}>
        {devices.map(dev => {
          const patient    = getPatientForDevice(dev);
          const live       = dev.latest;
          const isSelected = patient && String(selectedPatientId) === String(patient.patient_id);
          if (!patient) return (
            <div key={dev.device_uid} style={{ background: "#fff", borderRadius: ".75rem", padding: ".5rem .875rem", border: "1px solid #e0f2fe", opacity: 0.55, display: "flex", alignItems: "center", gap: ".5rem" }}>
              <span className="ms" style={{ color: "#9ca3af", fontSize: ".875rem" }}>sensors</span>
              <span style={{ fontSize: ".75rem", fontWeight: 600, color: "#9ca3af" }}>{dev.device_uid?.slice(-8)}</span>
              <span style={{ fontSize: ".65rem", color: "#f59e0b" }}>unassigned</span>
            </div>
          );
          return (
            <div key={dev.device_uid} onClick={() => onSelectPatient(patient)}
              style={{ background: "#fff", borderRadius: ".875rem", padding: ".625rem 1rem", border: isSelected ? "2px solid #3b82f6" : "1px solid #e0f2fe", cursor: "pointer", transition: "all .2s", boxShadow: isSelected ? "0 2px 12px rgba(59,130,246,.18)" : "0 1px 4px rgba(0,0,0,.05)", display: "flex", alignItems: "center", gap: ".75rem", flexShrink: 0, flex: "1 1 auto" }}>
              <div style={{ width: 30, height: 30, borderRadius: "50%", background: isSelected ? "#3b82f6" : "#eff6ff", color: isSelected ? "#fff" : "#3b82f6", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: ".8125rem", flexShrink: 0 }}>
                {patient.name.charAt(0)}
              </div>
              <div style={{ lineHeight: 1.3, minWidth: 0 }}>
                <p style={{ fontWeight: 700, fontSize: ".8125rem", color: "#111827", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{patient.name}</p>
                <p style={{ fontSize: ".65rem", color: "#9ca3af", margin: 0 }}>{dev.device_uid?.slice(-8)}</p>
              </div>
              {live && (
                <div style={{ display: "flex", gap: ".5rem", marginLeft: ".25rem", flexWrap: "wrap" }}>
                  {[
                    { k: "hr",        v: live.hr,          u: "bpm", col: "#ef4444" },
                    { k: "spo2",      v: live.spo2,        u: "%",   col: "#3b82f6" },
                    { k: "temp_body", v: live.temp_body,   u: "°C",  col: "#f59e0b" },
                  ].map(m => (
                    <div key={m.k} style={{ background: "#f9fafb", borderRadius: ".5rem", padding: ".2rem .45rem", textAlign: "center", minWidth: 44 }}>
                      <p style={{ fontSize: ".65rem", color: "#9ca3af", margin: 0, fontWeight: 600, textTransform: "uppercase", lineHeight: 1 }}>{VC[m.k]?.label?.split(" ")[0]}</p>
                      <p style={{ fontSize: ".8rem", fontWeight: 700, color: m.col, margin: 0, lineHeight: 1.3 }}>{fmtN(m.v)}<span style={{ fontSize: ".55rem", color: "#9ca3af", fontWeight: 400 }}> {m.u}</span></p>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: "flex", alignItems: "center", gap: ".25rem", marginLeft: "auto", flexShrink: 0 }}>
                <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#22c55e" }} className="pls" />
                <span style={{ fontSize: ".6rem", color: "#22c55e", fontWeight: 700 }}>LIVE</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// NOTIFICATION CARD — shared between desktop dropdown and mobile panel
// ════════════════════════════════════════════════════════════════════════════
function NotifCard({ n, idx, nb, onMarkSeen, onSendObservation, compact = false }) {
  // A notification is "from doctor" when subadmin_role === 'doctor'
  // OR when the note starts with "Dr." (fallback for older rows without role)
  const fromDoctor = n.subadmin_role === "doctor" || (n.note || "").startsWith("Dr.");
  const canSend    = fromDoctor && n.status !== "done";

  if (compact) {
    // Mobile compact card
    return (
      <div style={{ background: n.status === "pending" ? "#fef9f9" : "#fff", border: `1px solid ${n.status === "pending" ? "#fee2e2" : "#f3f4f6"}`, borderRadius: ".875rem", padding: ".875rem", marginBottom: ".5rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: ".5rem", marginBottom: ".375rem" }}>
          <span style={{ background: nb.bg, color: nb.c, fontSize: ".65rem", fontWeight: 700, padding: "1px 7px", borderRadius: "9999px" }}>{nb.label}</span>
          <span style={{ fontSize: ".75rem", fontWeight: 700, color: "#374151" }}>By {n.subadmin_name || "Doctor"}</span>
          {fromDoctor && (
            <span style={{ background: "#eff6ff", color: "#2563eb", fontSize: ".6rem", fontWeight: 700, padding: "1px 6px", borderRadius: "9999px" }}>
              👨‍⚕️ Doctor
            </span>
          )}
          {n.status === "pending" && (
            <button onClick={() => onMarkSeen(n.id)} style={{ marginLeft: "auto", background: "#eff6ff", color: "#3b82f6", border: "none", borderRadius: "6px", width: 28, height: 28, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <span className="ms" style={{ fontSize: ".8rem" }}>done</span>
            </button>
          )}
        </div>
        <p style={{ fontSize: ".8125rem", color: "#374151", margin: "0 0 .25rem", lineHeight: 1.5 }}>{n.note}</p>
        <p style={{ fontSize: ".7rem", color: "#9ca3af", margin: canSend ? "0 0 .5rem" : 0 }}>{fmtT(n.created_at)}</p>
        {/* ── Send Observation to Patient button ── */}
        {canSend && (
          <button className="notif-send-btn" onClick={() => onSendObservation(n)}>
            <span className="ms" style={{ fontSize: ".875rem" }}>send</span>
            Send Observation to Patient
          </button>
        )}
      </div>
    );
  }

  // Desktop full card
  return (
    <div style={{ background: n.status === "pending" ? "#fef9f9" : "#fff", border: `1px solid ${n.status === "pending" ? "#fee2e2" : n.status === "done" ? "#dcfce7" : "#f3f4f6"}`, borderRadius: "1rem", padding: "1rem", marginBottom: ".75rem" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: ".625rem", marginBottom: ".5rem" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: ".5rem", flexWrap: "wrap", marginBottom: ".25rem" }}>
            <span style={{ fontSize: ".7rem", fontWeight: 700, color: "#9ca3af" }}>Note {idx + 1}</span>
            <span style={{ background: nb.bg, color: nb.c, fontSize: ".65rem", fontWeight: 700, padding: "1px 7px", borderRadius: "9999px", display: "inline-flex", alignItems: "center", gap: ".25rem" }}>
              <span style={{ width: 5, height: 5, borderRadius: "50%", background: nb.dot }} />{nb.label}
            </span>
            {fromDoctor && (
              <span style={{ background: "#eff6ff", color: "#2563eb", fontSize: ".6rem", fontWeight: 700, padding: "1px 7px", borderRadius: "9999px", display: "inline-flex", alignItems: "center", gap: ".25rem" }}>
                <span className="ms" style={{ fontSize: ".65rem" }}>medical_services</span>Doctor Treatment
              </span>
            )}
          </div>
          <p style={{ fontSize: ".8125rem", color: "#374151", margin: 0, fontWeight: 600 }}>
            By <span style={{ color: "#3b82f6" }}>{n.subadmin_name || `SubAdmin #${n.subadmin_id}`}</span> about <span style={{ color: "#111827" }}>{n.patient_name || `Patient #${n.patient_id}`}</span>
          </p>
        </div>
        {n.status === "pending" && (
          <button onClick={() => onMarkSeen(n.id)} style={{ background: "#eff6ff", color: "#3b82f6", border: "none", borderRadius: "8px", width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <span className="ms" style={{ fontSize: ".875rem" }}>done</span>
          </button>
        )}
      </div>
      <p style={{ fontSize: ".8125rem", color: "#6b7280", margin: "0 0 .5rem", lineHeight: 1.6, background: "#f9fafb", padding: ".5rem .75rem", borderRadius: ".625rem", whiteSpace: "pre-wrap" }}>{n.note}</p>
      <div style={{ display: "flex", alignItems: "center", gap: ".375rem", marginBottom: canSend ? ".625rem" : 0 }}>
        <span className="ms" style={{ fontSize: ".75rem", color: "#d1d5db" }}>schedule</span>
        <span style={{ fontSize: ".75rem", color: "#9ca3af" }}>{fmtT(n.created_at)} · {timeAgo(n.created_at)}</span>
      </div>
      {/* ── Send Observation to Patient button ── */}
      {canSend && (
        <button className="notif-send-btn" onClick={() => onSendObservation(n)}>
          <span className="ms" style={{ fontSize: ".875rem" }}>send</span>
          Send Observation to Patient
        </button>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// AI CHATBOT COMPONENT
// ════════════════════════════════════════════════════════════════════════════
function AIChatbot({ patients = [], measurements = [], alerts = [], userId }) {
  const [selPatId, setSelPatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input,    setInput]    = useState("");
  const [loading,  setLoading]  = useState(false);
  const [aiStatus, setAiStatus] = useState(null);
  const endRef   = useRef(null);
  const inputRef = useRef(null);

  const selPat = patients.find(p => String(p.patient_id) === String(selPatId)) || patients[0];

  useEffect(() => {
    fetch(`${API}/ai/status`)
      .then(r => r.json())
      .then(d => setAiStatus(d))
      .catch(() => setAiStatus({ ok: false, message: "Cannot reach AI service" }));
  }, []);

  useEffect(() => {
    if (patients.length && !selPatId) setSelPatId(patients[0]?.patient_id);
  }, [patients]);

  useEffect(() => {
    if (selPatId) {
      setMessages([{
        role: "assistant",
        content: `🏥 **CareTech Medical AI** ready.\n\nAnalyzing **${selPat?.name || "this patient"}** with ${measurements.filter(m => String(m.patient_id) === String(selPatId)).length} vital records.\n\n**I can:**\n🔮 Predict future vitals (1-7 days)\n🏥 Detect diseases & conditions\n📊 Analyze trends & risks\n💊 Recommend treatments\n\n**Try:** "Predict 7 days" or "What diseases to watch for?"`,
        id: Date.now(),
      }]);
    }
  }, [selPatId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const patMeas    = measurements.filter(m => String(m.patient_id) === String(selPat?.patient_id));
  const latestMeas = patMeas.length ? patMeas[patMeas.length - 1] : null;

  const calculatePrediction = (vitalKey, hoursAhead = 168) => {
    const values = patMeas.map((m, idx) => ({ value: m[vitalKey], time: idx }))
      .filter(v => v.value != null && !isNaN(v.value));
    if (values.length < 3) return null;
    const n = values.length;
    const sumX  = values.reduce((s, v) => s + v.time, 0);
    const sumY  = values.reduce((s, v) => s + v.value, 0);
    const sumXY = values.reduce((s, v) => s + (v.time * v.value), 0);
    const sumX2 = values.reduce((s, v) => s + (v.time * v.time), 0);
    const slope     = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    const futureTime = n + (hoursAhead / 4);
    let predicted = intercept + slope * futureTime;
    const config = VC[vitalKey];
    if (config) predicted = Math.min(Math.max(predicted, config.warn[0] - 10), config.warn[1] + 10);
    const residuals  = values.map(v => v.value - (intercept + slope * v.time));
    const stdDev     = Math.sqrt(residuals.reduce((s, r) => s + r * r, 0) / n);
    const confidence = Math.max(50, Math.min(95, 100 - (stdDev / (Math.abs(predicted) || 1)) * 100));
    return {
      current: values[values.length - 1].value,
      predicted: parseFloat(predicted.toFixed(1)),
      trend: slope > 0.2 ? "rising" : slope < -0.2 ? "falling" : "stable",
      confidence: Math.round(confidence),
    };
  };

  const detectDiseases = () => {
    if (!latestMeas) return [];
    const diseases = [];
    if (latestMeas.hr) {
      if (latestMeas.hr > 100) diseases.push({ name: "Tachycardia", severity: latestMeas.hr > 120 ? "High" : "Moderate", evidence: `HR ${latestMeas.hr} bpm` });
      if (latestMeas.hr < 60)  diseases.push({ name: "Bradycardia", severity: latestMeas.hr < 50  ? "High" : "Moderate", evidence: `HR ${latestMeas.hr} bpm` });
    }
    if (latestMeas.spo2 && latestMeas.spo2 < 94)
      diseases.push({ name: "Hypoxemia", severity: latestMeas.spo2 < 90 ? "Severe" : "Moderate", evidence: `SpO₂ ${latestMeas.spo2}%` });
    if (latestMeas.temp_body) {
      if (latestMeas.temp_body > 38.0) diseases.push({ name: "Fever",       severity: latestMeas.temp_body > 39.0 ? "High" : "Moderate", evidence: `${latestMeas.temp_body}°C` });
      if (latestMeas.temp_body < 36.0) diseases.push({ name: "Hypothermia", severity: "Moderate", evidence: `${latestMeas.temp_body}°C` });
    }
    if (latestMeas.air_quality && latestMeas.air_quality > 1500)
      diseases.push({ name: "CO2 Exposure Risk", severity: latestMeas.air_quality > 2000 ? "Critical" : "High", evidence: `CO2 ${Math.round(latestMeas.air_quality)} ppm` });
    return diseases;
  };

  const generatePredictionResponse = (days = 7) => {
    const hours = days * 24;
    const predictions = [];
    for (const key of ['hr', 'spo2', 'temp_body']) {
      const pred = calculatePrediction(key, hours);
      if (pred && VC[key]) predictions.push({ label: VC[key].label, current: pred.current, predicted: pred.predicted, unit: VC[key].unit, trend: pred.trend, confidence: pred.confidence });
    }
    if (!predictions.length) return null;
    let response = `## 🔮 ${days}-DAY VITAL FORECAST\n\n`;
    for (const p of predictions) {
      const arrow = p.trend === "rising" ? "↑" : p.trend === "falling" ? "↓" : "→";
      response += `${arrow} **${p.label}**: ${p.current.toFixed(1)} → **${p.predicted.toFixed(1)}** ${p.unit} (${p.trend}, ${p.confidence}% confidence)\n`;
    }
    response += `\n### Clinical Interpretation:\n`;
    const hrP  = calculatePrediction('hr', hours);
    const spP  = calculatePrediction('spo2', hours);
    const tP   = calculatePrediction('temp_body', hours);
    if (hrP && hrP.predicted > 100)   response += `- ⚠️ **Tachycardia risk**: HR predicted to exceed 100 bpm.\n`;
    if (spP && spP.predicted < 94)    response += `- 🚨 **Hypoxia risk**: SpO₂ predicted to drop below 94%.\n`;
    if (tP  && tP.predicted  > 38.0)  response += `- 🔥 **Fever risk**: Temperature may rise above 38°C.\n`;
    if (hrP && hrP.predicted < 60)    response += `- ⚠️ **Bradycardia risk**: HR predicted to drop below 60 bpm.\n`;
    const rec = (hrP?.predicted > 100 || spP?.predicted < 94 || tP?.predicted > 38)
      ? "Increase monitoring frequency to q4h. Prepare interventions for predicted deterioration."
      : "Continue routine monitoring. Patient appears stable based on predictive trends.";
    response += `\n**Recommendation**: ${rec}\n`;
    return response;
  };

  const send = useCallback(async (text) => {
    const content = (text || input).trim();
    if (!content || loading) return;
    setInput("");
    const userMsg = { role: "user", content, id: Date.now() };
    const history = [...messages.filter(m => m.id !== "typing"), userMsg];
    setMessages(history);
    setLoading(true);
    setMessages(prev => [...prev, { role: "assistant", content: "🔮 Analyzing medical data…", id: "typing", isTyping: true }]);
    const isPrediction = /predict|forecast|will be|future|next \d+ days|\d+ day|7 days|tomorrow|trend/i.test(content);
    const isDisease    = /disease|condition|diagnosis|what (does|is)|suffer|illness|symptom/i.test(content);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: history.map(m => ({ role: m.role, content: m.content })), patientId: selPat?.patient_id }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.error || `HTTP ${res.status}`); }
      const data = await res.json();
      let aiResponse = data.content?.[0]?.text || "";
      if (aiResponse.length < 100) {
        if (isPrediction && patMeas.length >= 3) aiResponse = generatePredictionResponse(7);
        else if (isDisease && latestMeas) {
          const diseases = detectDiseases();
          aiResponse = diseases.length
            ? `## 🏥 DETECTED CONDITIONS\n\n${diseases.map(d => `- **${d.name}** (${d.severity}): ${d.evidence}`).join("\n")}\n\n**Recommendation**: Schedule clinical review.\n`
            : `## ✅ No acute conditions detected\n\nAll values within normal ranges.\n`;
        } else if (patMeas.length >= 3 && latestMeas) {
          const hr = calculatePrediction('hr', 24);
          aiResponse = `## 📊 CURRENT STATUS\n\n**Patient**: ${selPat?.name}\n**Readings**: ${patMeas.length} records\n\n- HR: ${fmtN(latestMeas.hr)} bpm ${hr?.trend === "rising" ? "↑" : hr?.trend === "falling" ? "↓" : "→"}\n- SpO₂: ${fmtN(latestMeas.spo2)}%\n- Temp: ${fmtN(latestMeas.temp_body)}°C\n- Air: ${latestMeas.air_quality != null ? getAirQualityLabel(latestMeas.air_quality) : "—"}\n`;
        } else {
          aiResponse = `Need at least 3 measurements for predictions. Current data: ${patMeas.length} records.`;
        }
      }
      if (!aiResponse.includes("Disclaimer") && !aiResponse.includes("disclaimer"))
        aiResponse += `\n\n---\n⚠️ **Medical AI Disclaimer**: AI-generated. Always verify with clinical judgment.`;
      setMessages(prev => [...prev.filter(m => m.id !== "typing"), { role: "assistant", content: aiResponse, id: Date.now() + 1 }]);
    } catch (err) {
      let fb = `⚠️ AI service: ${err.message}\n\n`;
      if (patMeas.length >= 3) fb += generatePredictionResponse(7) || "Unable to generate predictions.\n";
      else fb += `Need ≥3 measurements. Current: ${patMeas.length}\n`;
      setMessages(prev => [...prev.filter(m => m.id !== "typing"), { role: "assistant", content: fb, id: Date.now() + 2, error: true }]);
    } finally { setLoading(false); }
  }, [input, loading, messages, selPat, patMeas, latestMeas]);

  const handleKey = e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  function renderMd(text) {
    if (!text) return "";
    return text
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>")
      .replace(/^[•\-] (.+)$/gm, "<li style='margin:3px 0;padding-left:4px'>$1</li>")
      .replace(/(<li[^>]*>.*<\/li>)/gs, m => `<ul style='margin:6px 0;padding-left:16px'>${m}</ul>`)
      .replace(/\n\n/g, "</p><p style='margin:8px 0'>")
      .replace(/\n/g, "<br/>");
  }

  const patStatus = pid => {
    const meas = measurements.filter(m => String(m.patient_id) === String(pid));
    const last = meas.length ? meas.reduce((a, b) => new Date(a.created_at) > new Date(b.created_at) ? a : b) : null;
    if (!last) return "unknown";
    const statuses = Object.keys(VC).map(k => vStat(k, last[k]));
    if (statuses.includes("Abnormal")) return "Abnormal";
    if (statuses.includes("Watch"))    return "Watch";
    return "Normal";
  };

  const PROMPTS = ["Predict 7 days", "Detect diseases", "Health trends", "Risk assessment", "What to watch for?"];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      {aiStatus && !aiStatus.ok && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "1rem", padding: ".875rem 1.25rem", display: "flex", alignItems: "center", gap: ".75rem" }}>
          <span className="ms" style={{ color: "#ef4444", fontSize: "1.25rem" }}>warning</span>
          <div>
            <p style={{ fontWeight: 700, color: "#991b1b", margin: 0, fontSize: ".875rem" }}>AI Service Unavailable</p>
            <p style={{ color: "#b91c1c", fontSize: ".8125rem", margin: 0 }}>{aiStatus.message}</p>
          </div>
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: ".75rem", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: ".75rem", flexWrap: "wrap" }}>
          <span style={{ fontSize: ".8rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".06em" }}>Patient</span>
          {patients.map(p => {
            const st  = patStatus(p.patient_id); const ss = SS[st]; const sel = String(p.patient_id) === String(selPatId);
            return (
              <button key={p.patient_id} onClick={() => setSelPatId(p.patient_id)}
                style={{ padding: ".45rem 1rem", borderRadius: "9999px", border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: ".8125rem", fontWeight: 600, display: "flex", alignItems: "center", gap: ".4rem", background: sel ? ss.bg : "#fff", color: sel ? ss.c : "#374151", boxShadow: sel ? `0 0 0 2px ${ss.dot}33` : "0 1px 4px rgba(0,0,0,.08)" }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: sel ? ss.dot : "#d1d5db", flexShrink: 0 }} />{p.name}
              </button>
            );
          })}
        </div>
        <div style={{ background: "linear-gradient(135deg,#8b5cf6,#06b6d4)", borderRadius: "9999px", padding: ".3rem 1rem", display: "flex", alignItems: "center", gap: ".5rem" }}>
          <span style={{ fontSize: "1rem" }}>🔮</span>
          <span style={{ fontSize: ".75rem", fontWeight: 700, color: "#fff" }}>PREDICTION MODE</span>
        </div>
      </div>

      {selPat && (
        <div className="ai-grid" style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: "1.25rem", alignItems: "start" }}>
          <div style={{ background: "#fff", borderRadius: "1.5rem", boxShadow: "0 4px 20px -2px rgba(0,0,0,.07)", display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 580 }}>
            <div style={{ padding: "1rem 1.25rem", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", gap: ".875rem", background: "linear-gradient(135deg,#eff6ff,#f0fdf4)" }}>
              <div style={{ width: 44, height: 44, borderRadius: "14px", background: "linear-gradient(135deg,#8b5cf6,#06b6d4)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <span className="ms" style={{ color: "#fff", fontSize: "1.25rem" }}>smart_toy</span>
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ fontWeight: 700, color: "#111827", margin: 0, fontSize: ".9375rem" }}>CareTech Medical AI</p>
                <p style={{ fontSize: ".75rem", color: "#6b7280", margin: 0 }}>🔮 {patMeas.length} readings · {selPat.name}</p>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: ".375rem", background: aiStatus?.ok ? "#f0fdf4" : "#fef2f2", border: `1px solid ${aiStatus?.ok ? "#bbf7d0" : "#fecaca"}`, borderRadius: "9999px", padding: ".25rem .75rem" }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: aiStatus?.ok ? "#22c55e" : "#ef4444" }} className="pls" />
                <span style={{ fontSize: ".7rem", fontWeight: 700, color: aiStatus?.ok ? "#166534" : "#991b1b" }}>{aiStatus?.ok ? "Active" : "Offline"}</span>
              </div>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "1rem 1.25rem", display: "flex", flexDirection: "column", gap: "1rem", minHeight: 360, maxHeight: 420 }}>
              {messages.map(msg => (
                <div key={msg.id} style={{ display: "flex", gap: ".625rem", alignItems: "flex-start", flexDirection: msg.role === "user" ? "row-reverse" : "row" }}>
                  {msg.role === "assistant" && <div style={{ width: 32, height: 32, borderRadius: "10px", background: "linear-gradient(135deg,#8b5cf6,#06b6d4)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: "2px" }}><span className="ms" style={{ color: "#fff", fontSize: ".875rem" }}>smart_toy</span></div>}
                  {msg.role === "user" && <div style={{ width: 32, height: 32, borderRadius: "10px", background: "#3b82f6", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: "#fff", fontWeight: 700, fontSize: ".8125rem", marginTop: "2px" }}>{userId?.toString().charAt(0) || "U"}</div>}
                  <div style={{ maxWidth: "82%", background: msg.role === "user" ? "#3b82f6" : msg.error ? "#fef2f2" : msg.isTyping ? "#f3f4f6" : "#f9fafb", color: msg.role === "user" ? "#fff" : msg.error ? "#991b1b" : "#111827", borderRadius: msg.role === "user" ? "1rem 1rem 0 1rem" : "1rem 1rem 1rem 0", padding: ".75rem 1rem", fontSize: ".875rem", lineHeight: 1.65, border: msg.error ? "1px solid #fecaca" : "none" }}>
                    {msg.isTyping
                      ? <div style={{ display: "flex", gap: "4px", alignItems: "center", height: 16 }}><span style={{ fontSize: ".875rem" }}>🔮</span>{[0, 1, 2].map(i => <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: "#9ca3af", animation: `bounce 1.2s ease-in-out ${i * .2}s infinite` }} />)}<span style={{ fontSize: ".7rem", color: "#6b7280", marginLeft: "4px" }}>Calculating...</span></div>
                      : msg.role === "assistant"
                        ? <div dangerouslySetInnerHTML={{ __html: `<p style='margin:0'>${renderMd(msg.content)}</p>` }} />
                        : <p style={{ margin: 0 }}>{msg.content}</p>
                    }
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>
            <div style={{ padding: "0 1.25rem .75rem", display: "flex", flexWrap: "wrap", gap: ".5rem", borderBottom: "1px solid #f3f4f6" }}>
              <span style={{ fontSize: ".7rem", color: "#8b5cf6", fontWeight: 700, marginRight: ".25rem" }}>🔮 QUICK:</span>
              {PROMPTS.map(q => <button key={q} onClick={() => send(q)} style={{ padding: ".35rem .875rem", background: "#f5f3ff", color: "#6d28d9", border: "1px solid #ddd6fe", borderRadius: "9999px", fontSize: ".75rem", fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>{q}</button>)}
            </div>
            <div style={{ padding: "1rem 1.25rem", borderTop: "1px solid #f3f4f6", display: "flex", gap: ".75rem", alignItems: "flex-end" }}>
              <textarea ref={inputRef} value={input} onChange={e => { setInput(e.target.value); e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px"; }}
                onKeyDown={handleKey} placeholder={`Ask about ${selPat.name}…`} rows={1} disabled={loading}
                style={{ flex: 1, border: "1.5px solid #e5e7eb", borderRadius: ".875rem", padding: ".625rem .875rem", fontSize: ".875rem", fontFamily: "inherit", resize: "none", outline: "none", lineHeight: 1.5, maxHeight: 120, minHeight: 40, background: loading ? "#f9fafb" : "#fff", color: "#111827" }} />
              <button onClick={() => send()} disabled={loading || !input.trim()}
                style={{ width: 40, height: 40, borderRadius: "12px", border: "none", cursor: loading || !input.trim() ? "not-allowed" : "pointer", background: loading || !input.trim() ? "#f3f4f6" : "#8b5cf6", color: loading || !input.trim() ? "#9ca3af" : "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: loading || !input.trim() ? "none" : "0 4px 12px rgba(139,92,246,.4)" }}>
                {loading ? <div style={{ width: 16, height: 16, border: "2px solid #9ca3af", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }} /> : <span className="ms" style={{ fontSize: "1.125rem" }}>send</span>}
              </button>
            </div>
          </div>

          <div className="ai-sidebar" style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <div style={{ background: "#fff", borderRadius: "1.5rem", boxShadow: "0 4px 20px -2px rgba(0,0,0,.07)", padding: "1.25rem" }}>
              <h3 style={{ fontWeight: 700, color: "#111827", margin: "0 0 1rem", fontSize: ".9375rem" }}>🔮 Current Vitals</h3>
              {!latestMeas
                ? <p style={{ color: "#9ca3af", fontSize: ".875rem", textAlign: "center", padding: "1rem 0" }}>No measurements yet</p>
                : <div style={{ display: "flex", flexDirection: "column", gap: ".5rem" }}>
                    {Object.entries(VC).map(([k, c]) => {
                      const v = latestMeas[k]; if (v == null) return null;
                      const n = +v; const status = vStat(k, n); const ss2 = SS[status];
                      const pred7 = calculatePrediction(k, 168);
                      return (
                        <div key={k} style={{ background: "#f9fafb", borderRadius: ".875rem", padding: ".625rem .875rem" }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: ".375rem" }}>
                            <span style={{ fontSize: ".75rem", fontWeight: 600, color: "#6b7280" }}>{c.label}</span>
                            <span style={{ background: ss2.bg, color: ss2.c, fontSize: ".6rem", fontWeight: 700, padding: "1px 6px", borderRadius: "9999px" }}>{status}</span>
                          </div>
                          <div style={{ display: "flex", alignItems: "baseline", gap: ".25rem", marginBottom: ".375rem" }}>
                            <span style={{ fontSize: "1.25rem", fontWeight: 700, color: "#111827" }}>{k === "air_quality" ? n.toFixed(0) : fmtN(v)}</span>
                            <span style={{ fontSize: ".75rem", color: "#9ca3af" }}>{c.unit}</span>
                            {k === "air_quality" && <span style={{ fontSize: ".7rem", color: "#6b7280", marginLeft: ".25rem" }}>({getAirQualityLabel(v)})</span>}
                          </div>
                          {pred7 && <div style={{ fontSize: ".7rem", color: pred7.predicted > c.normal[1] || pred7.predicted < c.normal[0] ? "#ef4444" : "#10b981", marginTop: ".25rem", borderTop: "1px solid #e5e7eb", paddingTop: ".25rem" }}>🔮 7d: {pred7.predicted.toFixed(1)} {c.unit} ({pred7.trend})</div>}
                        </div>
                      );
                    })}
                  </div>
              }
            </div>
            <div style={{ background: "linear-gradient(135deg,#f5f3ff,#ede9fe)", borderRadius: "1.5rem", border: "1px solid #ddd6fe", padding: "1.25rem" }}>
              <p style={{ fontWeight: 700, color: "#5b21b6", margin: "0 0 .625rem", fontSize: ".875rem" }}>🔮 Prediction Examples</p>
              {["Predict vitals next 7 days", "What diseases to watch for?", "Will condition worsen?", "Health risk assessment"].map(q => (
                <button key={q} onClick={() => send(q)} style={{ display: "block", width: "100%", marginBottom: ".4rem", padding: ".5rem .875rem", background: "#fff", color: "#6d28d9", border: "1px solid #ddd6fe", borderRadius: ".875rem", fontSize: ".8125rem", fontWeight: 600, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>🔮 {q} →</button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// AVATAR
// ════════════════════════════════════════════════════════════════════════════
function Avatar({ name, size = 36, bg = "#3b82f6" }) {
  const initials = (name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: bg, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: size * .35, flexShrink: 0, lineHeight: 1 }}>
      {initials}
    </div>
  );
}

const fmtTime = d => {
  if (!d) return "";
  const date = new Date(d), now = new Date();
  const diff = Math.floor((now - date) / 86400000);
  if (diff === 0) return date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  if (diff === 1) return "Yesterday";
  if (diff < 7)  return date.toLocaleDateString("en-GB", { weekday: "short" });
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

const editSecondsLeft = createdAt => {
  const age = (Date.now() - new Date(createdAt).getTime()) / 1000;
  return Math.max(0, 900 - age);
};

// ════════════════════════════════════════════════════════════════════════════
// MESSAGE BUBBLE
// ════════════════════════════════════════════════════════════════════════════
function MessageBubble({ m, isMe, peer, userId, onReply, onDelete, onEdit }) {
  const [hover,    setHover]    = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editText, setEditText] = useState(m.text || "");
  const [editLeft, setEditLeft] = useState(() => Math.floor(editSecondsLeft(m.created_at)));
  const editRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!isMe || m.deleted) return;
    const id = setInterval(() => {
      const s = Math.floor(editSecondsLeft(m.created_at));
      setEditLeft(s);
      if (s === 0) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [isMe, m.created_at, m.deleted]);

  useEffect(() => {
    if (!menuOpen) return;
    const h = e => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [menuOpen]);

  useEffect(() => { if (editMode) { editRef.current?.focus(); editRef.current?.select(); } }, [editMode]);

  const canEdit   = isMe && !m.deleted && editLeft > 0 && !m.optimistic;
  const canDelete = isMe && !m.deleted && !m.optimistic;
  const fmtCountdown = s => { const mm = Math.floor(s / 60); const ss = s % 60; return `${mm}:${String(ss).padStart(2,"0")}`; };

  const submitEdit = () => {
    if (editText.trim() && editText.trim() !== m.text) onEdit(m.id, editText.trim());
    setEditMode(false);
  };
  const handleEditKey = e => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submitEdit(); }
    if (e.key === "Escape") { setEditMode(false); setEditText(m.text || ""); }
  };

  if (m.deleted) {
    return (
      <div style={{display:"flex",flexDirection:isMe?"row-reverse":"row",alignItems:"center",gap:".5rem",marginBottom:".375rem"}}>
        {!isMe && <Avatar name={peer.username} size={26} bg={pickColor(peer.username)}/>}
        <div style={{display:"flex",alignItems:"center",gap:".375rem",background:"#f3f4f6",borderRadius:"1rem",padding:".5rem .9rem",opacity:.65}}>
          <span className="ms" style={{fontSize:".875rem",color:"#9ca3af"}}>block</span>
          <span style={{fontSize:".8125rem",color:"#9ca3af",fontStyle:"italic"}}>Message deleted</span>
        </div>
      </div>
    );
  }

  const replyAuthorLabel = () => {
    if (!m.reply_to_sender_name) return isMe ? peer.username : "You";
    if (isMe) return m.reply_to_sender_name === peer.username ? peer.username : "You";
    return m.reply_to_sender_name === peer.username ? "You" : m.reply_to_sender_name;
  };

  return (
    <div style={{display:"flex",flexDirection:isMe?"row-reverse":"row",alignItems:"flex-end",gap:".5rem",marginBottom:".25rem"}}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      {!isMe && <div style={{width:26,flexShrink:0}}><Avatar name={peer.username} size={26} bg={pickColor(peer.username)}/></div>}
      <div style={{maxWidth:"72%",display:"flex",flexDirection:"column",alignItems:isMe?"flex-end":"flex-start",position:"relative"}}>
        {m.reply_to_id && (
          <div style={{display:"flex",flexDirection:isMe?"row-reverse":"row",alignItems:"stretch",gap:0,marginBottom:"2px",opacity:.85}}>
            <div style={{width:3,borderRadius:"9999px",background:isMe?"rgba(255,255,255,0.5)":"#93c5fd",flexShrink:0,marginRight:isMe?0:"6px",marginLeft:isMe?"6px":0}}/>
            <div style={{background:isMe?"rgba(59,130,246,0.18)":"#e8f3ff",borderRadius:isMe?"10px 10px 0 10px":"10px 10px 10px 0",padding:"5px 10px",maxWidth:220,minWidth:60}}>
              <span style={{display:"block",fontSize:".68rem",fontWeight:700,color:isMe?"rgba(255,255,255,0.9)":"#2563eb",marginBottom:"1px",whiteSpace:"nowrap"}}>{replyAuthorLabel()}</span>
              <span style={{display:"block",fontSize:".72rem",color:isMe?"rgba(255,255,255,0.75)":"#4b6fa8",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                {m.reply_to_deleted ? "🚫 Message deleted" : (m.reply_to_text || "").slice(0,80) + ((m.reply_to_text||"").length > 80 ? "…" : "")}
              </span>
            </div>
          </div>
        )}
        <div style={{background:isMe?"#3b82f6":"#fff",color:isMe?"#fff":"#111827",borderRadius:isMe?"1.125rem 1.125rem 0.25rem 1.125rem":"1.125rem 1.125rem 1.125rem 0.25rem",padding:".625rem .9rem",fontSize:".875rem",lineHeight:1.5,boxShadow:"0 1px 2px rgba(0,0,0,.07)",opacity:m.optimistic?.7:1,border:isMe?"none":"0.5px solid #e5e7eb",wordBreak:"break-word",position:"relative"}}>
          {editMode ? (
            <div>
              <textarea ref={editRef} value={editText} onChange={e => setEditText(e.target.value)} onKeyDown={handleEditKey} rows={2}
                style={{width:"100%",minWidth:180,background:"rgba(255,255,255,.15)",border:"1px solid rgba(255,255,255,.5)",borderRadius:".5rem",color:"#fff",fontSize:".875rem",fontFamily:"inherit",padding:".35rem .5rem",resize:"none",outline:"none",lineHeight:1.4,boxSizing:"border-box"}}/>
              <div style={{display:"flex",gap:".375rem",marginTop:".375rem",justifyContent:"flex-end"}}>
                <button onClick={() => { setEditMode(false); setEditText(m.text||""); }} style={{background:"rgba(255,255,255,.2)",border:"none",borderRadius:".5rem",color:"#fff",fontSize:".75rem",padding:".25rem .625rem",cursor:"pointer",fontFamily:"inherit"}}>Cancel</button>
                <button onClick={submitEdit} style={{background:"#fff",border:"none",borderRadius:".5rem",color:"#3b82f6",fontSize:".75rem",fontWeight:700,padding:".25rem .625rem",cursor:"pointer",fontFamily:"inherit"}}>Save</button>
              </div>
            </div>
          ) : (
            <>
              <p style={{margin:"0 0 .25rem",lineHeight:1.55}}>{m.text}</p>
              <div style={{display:"flex",alignItems:"center",justifyContent:"flex-end",gap:".3rem",flexWrap:"wrap"}}>
                {m.edited_at && !m.optimistic && <span style={{fontSize:".6rem",color:isMe?"rgba(255,255,255,.6)":"#9ca3af",fontStyle:"italic"}}>Edited</span>}
                <span style={{fontSize:".62rem",color:isMe?"rgba(255,255,255,.65)":"#9ca3af"}}>{fmtTime(m.created_at)}</span>
                {isMe && <span className="ms" style={{fontSize:".75rem",color:m.seen?"#93c5fd":"rgba(255,255,255,.5)"}}>{m.seen?"done_all":"done"}</span>}
              </div>
            </>
          )}
        </div>
        {!editMode && hover && (
          <div style={{position:"absolute",[isMe?"left":"right"]:"-76px",bottom:"6px",display:"flex",flexDirection:"row",gap:"4px",alignItems:"center",zIndex:20}}>
            <button onClick={() => onReply({ id: m.id, text: m.text, sender: isMe ? "You" : peer.username })} title="Reply"
              style={{width:28,height:28,borderRadius:"50%",background:"#e5e7eb",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:"#6b7280",padding:0,boxShadow:"0 1px 3px rgba(0,0,0,.1)"}}
              onMouseEnter={e=>e.currentTarget.style.background="#d1d5db"} onMouseLeave={e=>e.currentTarget.style.background="#e5e7eb"}>
              <span className="ms" style={{fontSize:".875rem"}}>reply</span>
            </button>
            {isMe && (canEdit || canDelete) && (
              <div ref={menuRef} style={{position:"relative"}}>
                <button onClick={() => setMenuOpen(o=>!o)} title="Options"
                  style={{width:28,height:28,borderRadius:"50%",background:"#e5e7eb",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:"#6b7280",padding:0,boxShadow:"0 1px 3px rgba(0,0,0,.1)"}}
                  onMouseEnter={e=>e.currentTarget.style.background="#d1d5db"} onMouseLeave={e=>e.currentTarget.style.background="#e5e7eb"}>
                  <span className="ms" style={{fontSize:".875rem"}}>more_vert</span>
                </button>
                {menuOpen && (
                  <div style={{position:"absolute",bottom:"calc(100% + 6px)",left:"50%",transform:"translateX(-50%)",background:"#fff",borderRadius:"1rem",boxShadow:"0 8px 28px rgba(0,0,0,.16)",padding:".375rem",minWidth:160,zIndex:60,border:"0.5px solid #e5e7eb"}}>
                    {canEdit && (
                      <button onClick={() => { setMenuOpen(false); setEditMode(true); }}
                        style={{display:"flex",alignItems:"center",gap:".5rem",width:"100%",padding:".5rem .75rem",background:"none",border:"none",cursor:"pointer",fontFamily:"inherit",fontSize:".8125rem",fontWeight:600,color:"#374151",borderRadius:".625rem",textAlign:"left"}}
                        onMouseEnter={e=>e.currentTarget.style.background="#f3f4f6"} onMouseLeave={e=>e.currentTarget.style.background="none"}>
                        <span className="ms" style={{fontSize:"1rem",color:"#3b82f6"}}>edit</span>Edit
                        <span style={{marginLeft:"auto",fontSize:".68rem",color:"#9ca3af",fontVariantNumeric:"tabular-nums"}}>{fmtCountdown(editLeft)}</span>
                      </button>
                    )}
                    {canDelete && (
                      <button onClick={() => { setMenuOpen(false); onDelete(m.id); }}
                        style={{display:"flex",alignItems:"center",gap:".5rem",width:"100%",padding:".5rem .75rem",background:"none",border:"none",cursor:"pointer",fontFamily:"inherit",fontSize:".8125rem",fontWeight:600,color:"#ef4444",borderRadius:".625rem",textAlign:"left"}}
                        onMouseEnter={e=>e.currentTarget.style.background="#fef2f2"} onMouseLeave={e=>e.currentTarget.style.background="none"}>
                        <span className="ms" style={{fontSize:"1rem"}}>delete</span>Delete
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// CONTACT ROW
// ════════════════════════════════════════════════════════════════════════════
function ContactRow({ c, userId, peer, unread, onOpen }) {
  const isActive = peer?.id === c.id;
  const cnt = unread[c.id] || 0;
  const col = pickColor(c.username);
  return (
    <div onClick={() => onOpen(c)} style={{padding:".75rem 1rem",cursor:"pointer",display:"flex",alignItems:"center",gap:".75rem",background:isActive?"#eff6ff":"transparent",borderLeft:isActive?"3px solid #3b82f6":"3px solid transparent",borderBottom:"1px solid #f9fafb",transition:"all .12s"}}>
      <div style={{position:"relative",flexShrink:0}}>
        <Avatar name={c.username} size={38} bg={col}/>
        <span style={{position:"absolute",bottom:1,right:1,width:8,height:8,borderRadius:"50%",background:"#22c55e",border:"2px solid #fff"}}/>
      </div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:".25rem"}}>
          <p style={{fontWeight:isActive||cnt>0?700:600,color:"#111827",margin:0,fontSize:".875rem",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.username}</p>
          {c.last_message_at && <span style={{fontSize:".65rem",color:"#9ca3af",flexShrink:0}}>{fmtTime(c.last_message_at)}</span>}
        </div>
        <p style={{fontSize:".75rem",color:cnt>0?"#3b82f6":"#9ca3af",margin:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontWeight:cnt>0?600:400}}>
          {c.last_message?(c.last_message_from_me?"You: ":"")+c.last_message:c.role}
        </p>
      </div>
      {cnt > 0 && <span style={{background:"#3b82f6",color:"#fff",fontSize:".65rem",fontWeight:700,minWidth:18,height:18,borderRadius:"9999px",display:"flex",alignItems:"center",justifyContent:"center",padding:"0 5px",flexShrink:0}}>{cnt}</span>}
    </div>
  );
}

function ContactsSidebar({ filtContacts, peer, unread, totalUnread, search, setSearch, userId, onOpen }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ padding: "1.125rem 1rem", borderBottom: "1px solid #f3f4f6" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: ".875rem" }}>
          <h2 style={{ fontWeight: 800, fontSize: "1rem", color: "#111827", margin: 0 }}>Messages</h2>
          {totalUnread > 0 && <span style={{ background: "#ef4444", color: "#fff", fontSize: ".65rem", fontWeight: 700, padding: "2px 8px", borderRadius: "9999px" }}>{totalUnread}</span>}
        </div>
        <div style={{ position: "relative" }}>
          <span className="ms" style={{ position: "absolute", left: ".75rem", top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: "1rem" }}>search</span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…" style={{ width: "100%", boxSizing: "border-box", padding: ".45rem .75rem .45rem 2.25rem", border: "1.5px solid #e5e7eb", borderRadius: ".875rem", fontSize: ".8125rem", fontFamily: "inherit", outline: "none", background: "#f9fafb", color: "#111827" }} />
        </div>
      </div>
      <div style={{ flex: 1, overflowY: "auto" }}>
        {filtContacts.length === 0
          ? <div style={{ padding: "2rem 1rem", textAlign: "center" }}><span className="ms" style={{ fontSize: "2rem", color: "#e5e7eb", display: "block", marginBottom: ".5rem" }}>chat_bubble</span><p style={{ color: "#9ca3af", fontSize: ".8125rem", margin: 0 }}>No contacts yet</p></div>
          : filtContacts.map(c => <ContactRow key={c.id} c={c} userId={userId} peer={peer} unread={unread} onOpen={onOpen} />)
        }
      </div>
    </div>
  );
}

function ChatMessagePanel({ peer, grouped, userId, input, setInput, sending, replyingTo, setReplyingTo, handleKey, sendMessage, handleDelete, handleEdit, onBack, endRef, inputRef }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
      <div style={{ padding: ".875rem 1.25rem", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", gap: ".875rem", background: "#fff" }}>
        <button onClick={onBack} style={{ width: 32, height: 32, borderRadius: "8px", border: "none", background: "#f3f4f6", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280" }}>
          <span className="ms" style={{ fontSize: ".875rem" }}>arrow_back</span>
        </button>
        <div style={{ position: "relative" }}>
          <Avatar name={peer.username} size={36} bg={pickColor(peer.username)} />
          <span style={{ position: "absolute", bottom: 1, right: 1, width: 7, height: 7, borderRadius: "50%", background: "#22c55e", border: "2px solid #fff" }} />
        </div>
        <div style={{ flex: 1 }}>
          <p style={{ fontWeight: 700, color: "#111827", margin: 0, fontSize: ".9375rem" }}>{peer.username}</p>
          <p style={{ fontSize: ".72rem", color: "#6b7280", margin: 0, textTransform: "capitalize" }}>{peer.role}</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: ".375rem", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "9999px", padding: ".3rem .7rem" }}>
          <span className="ms" style={{ fontSize: ".8rem", color: "#16a34a" }}>edit</span>
          <span style={{ fontSize: ".68rem", color: "#16a34a", fontWeight: 600 }}>15 min</span>
        </div>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "1.25rem 1.25rem 1rem", display: "flex", flexDirection: "column", gap: ".125rem", background: "#fafafa", minHeight: 300, maxHeight: 460 }}>
        {grouped.length === 0 && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: ".75rem", opacity: .6 }}>
            <Avatar name={peer.username} size={56} bg={pickColor(peer.username)} />
            <p style={{ color: "#9ca3af", fontSize: ".875rem" }}>Start a conversation with {peer.username}</p>
          </div>
        )}
        {grouped.map((item, i) => {
          if (item.type === "date") return (
            <div key={"d" + i} style={{ textAlign: "center", margin: ".875rem 0 .375rem" }}>
              <span style={{ background: "#e5e7eb", color: "#6b7280", fontSize: ".68rem", fontWeight: 600, padding: "3px 12px", borderRadius: "9999px" }}>{item.label}</span>
            </div>
          );
          const m = item.msg; const isMe = String(m.sender_id) === String(userId);
          return <MessageBubble key={m.id || i} m={m} isMe={isMe} peer={peer} userId={userId} onReply={setReplyingTo} onDelete={handleDelete} onEdit={handleEdit} />;
        })}
        <div ref={endRef} />
      </div>
      {replyingTo && (
        <div style={{ padding: ".5rem 1rem", background: "#eff6ff", borderTop: "1px solid #dbeafe", display: "flex", alignItems: "center", gap: ".625rem" }}>
          <div style={{ width: 3, borderRadius: "9999px", background: "#3b82f6", flexShrink: 0, alignSelf: "stretch", minHeight: 28 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: ".7rem", fontWeight: 700, color: "#2563eb", margin: "0 0 1px" }}>Replying to {replyingTo.sender}</p>
            <p style={{ fontSize: ".75rem", color: "#6b7280", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{replyingTo.text}</p>
          </div>
          <button onClick={() => setReplyingTo(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", padding: "2px", display: "flex", alignItems: "center", flexShrink: 0 }}>
            <span className="ms" style={{ fontSize: "1.125rem" }}>close</span>
          </button>
        </div>
      )}
      <div style={{ padding: ".75rem 1rem", borderTop: "1px solid #f3f4f6", display: "flex", alignItems: "flex-end", gap: ".625rem", background: "#fff" }}>
        <textarea ref={inputRef} value={input} onChange={e => { setInput(e.target.value); e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 100) + "px"; }} onKeyDown={handleKey}
          placeholder={`Message ${peer.username}…`} rows={1}
          style={{ flex: 1, border: "1.5px solid #e5e7eb", borderRadius: "1.125rem", padding: ".5rem .9rem", fontSize: ".875rem", fontFamily: "inherit", resize: "none", outline: "none", lineHeight: 1.5, maxHeight: 100, minHeight: 38, background: "#f9fafb", color: "#111827", transition: "border-color .15s" }}
          onFocus={e => e.target.style.borderColor = "#93c5fd"} onBlur={e => e.target.style.borderColor = "#e5e7eb"} />
        <button onClick={sendMessage} disabled={!input.trim() || sending}
          style={{ width: 38, height: 38, borderRadius: "11px", border: "none", cursor: !input.trim() || sending ? "not-allowed" : "pointer", background: !input.trim() || sending ? "#f3f4f6" : "#3b82f6", color: !input.trim() || sending ? "#9ca3af" : "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: !input.trim() || sending ? "none" : "0 4px 12px rgba(59,130,246,.35)", transition: "all .15s" }}>
          {sending ? <div style={{ width: 14, height: 14, border: "2px solid #9ca3af", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }} /> : <span className="ms" style={{ fontSize: "1.1rem" }}>send</span>}
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// HOSPITAL CHAT — mobile-aware
// ════════════════════════════════════════════════════════════════════════════
function HospitalChat({ userId, hospitalId }) {
  const [contacts,   setContacts]   = useState([]);
  const [peer,       setPeer]       = useState(null);
  const [messages,   setMessages]   = useState([]);
  const [input,      setInput]      = useState("");
  const [unread,     setUnread]     = useState({});
  const [search,     setSearch]     = useState("");
  const [sending,    setSending]    = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [mobileView, setMobileView] = useState("contacts");
  const endRef   = useRef(null);
  const inputRef = useRef(null);
  const pollRef  = useRef(null);

  const fetchContacts = useCallback(async () => {
    try { const r = await fetch(`${API}/chat/contacts?userId=${userId}&hospitalId=${hospitalId}`); if (!r.ok) throw new Error(); const data = await r.json(); setContacts(Array.isArray(data) ? data : []); } catch { setContacts([]); }
  }, [userId, hospitalId]);

  const fetchMessages = useCallback(async (peerId) => {
    if (!peerId) return;
    try { const r = await fetch(`${API}/chat/messages?userId=${userId}&peerId=${peerId}`); if (!r.ok) { if (r.status === 403) setPeer(null); return; } const data = await r.json(); setMessages(Array.isArray(data) ? data : []); } catch { setMessages([]); }
  }, [userId]);

  const fetchUnread = useCallback(async () => {
    try { const r = await fetch(`${API}/chat/unread?userId=${userId}`); if (!r.ok) throw new Error(); const data = await r.json(); const map = {}; (Array.isArray(data) ? data : []).forEach(row => { map[row.sender_id] = +row.count; }); setUnread(map); } catch {}
  }, [userId]);

  useEffect(() => {
    fetchContacts(); fetchUnread();
    pollRef.current = setInterval(() => { fetchContacts(); fetchUnread(); if (peer) fetchMessages(peer.id); }, 4000);
    return () => clearInterval(pollRef.current);
  }, [fetchContacts, fetchUnread, peer, fetchMessages]);

  const markSeen = useCallback(async (peerId, msgs) => {
    const unseen = (msgs || messages).filter(m => String(m.sender_id) === String(peerId) && !m.seen && !m.deleted);
    if (!unseen.length) return;
    try {
      await Promise.all(unseen.map(m => fetch(`${API}/chat/messages/${m.id}/seen`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ actorId: parseInt(userId), text: m.text }) })));
      setMessages(prev => prev.map(m => String(m.sender_id) === String(peerId) ? { ...m, seen: 1 } : m));
      setUnread(prev => ({ ...prev, [peerId]: 0 }));
    } catch {}
  }, [messages, userId]);

  useEffect(() => { if (peer && messages.length) markSeen(peer.id, messages); }, [messages.length, peer?.id]); // eslint-disable-line
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const openChat = async (contact) => {
    setPeer(contact); setMobileView("chat");
    await fetchMessages(contact.id);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const goBackToContacts = () => { setMobileView("contacts"); setPeer(null); setReplyingTo(null); };

  const sendMessage = async () => {
    const text = input.trim(); if (!text || !peer || sending) return; setInput(""); setSending(true);
    const opt = { id: "opt_" + Date.now(), sender_id: parseInt(userId), receiver_id: peer.id, text, seen: 0, created_at: new Date().toISOString(), optimistic: true, reply_to_id: replyingTo?.id || null, reply_to_text: replyingTo?.text || "", reply_to_sender_name: replyingTo?.sender || "", reply_to_deleted: 0, deleted: 0, edited_at: null };
    setMessages(prev => [...prev, opt]); setReplyingTo(null);
    try {
      const r = await fetch(`${API}/chat/messages`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ sender_id: parseInt(userId), receiver_id: peer.id, text, reply_to_id: replyingTo?.id || null }) });
      if (r.ok) { const saved = await r.json(); setMessages(prev => prev.map(m => m.id === opt.id ? saved : m)); }
      else { setMessages(prev => prev.filter(m => m.id !== opt.id)); const err = await r.json(); alert(err.error || "Failed"); }
    } catch { setMessages(prev => prev.filter(m => m.id !== opt.id)); alert("Failed"); }
    finally { setSending(false); }
  };

  const handleDelete = async (msgId) => { if (!window.confirm("Delete this message?")) return; try { const r = await fetch(`${API}/chat/messages/${msgId}`, { method: "DELETE", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ actorId: parseInt(userId) }) }); if (r.ok) { setMessages(prev => prev.map(m => m.id === msgId ? { ...m, deleted: 1, text: "" } : m)); } else { const d = await r.json(); alert(d.error || "Could not delete"); } } catch { alert("Failed"); } };
  const handleEdit  = async (msgId, newText) => { try { const r = await fetch(`${API}/chat/messages/${msgId}/edit`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ actorId: parseInt(userId), text: newText }) }); const d = await r.json(); if (r.ok) { setMessages(prev => prev.map(m => m.id === msgId ? { ...m, text: newText, edited_at: d.edited_at } : m)); } else { alert(d.error || "Could not edit"); } } catch { alert("Failed"); } };
  const handleKey = e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } };

  const totalUnread  = Object.values(unread).reduce((a, b) => a + b, 0);
  const filtContacts = contacts.filter(c => String(c.id) !== String(userId) && (!search || c.username.toLowerCase().includes(search.toLowerCase())));
  const grouped = []; let lastDate = null;
  messages.forEach(m => { const d = new Date(m.created_at).toLocaleDateString(); if (d !== lastDate) { grouped.push({ type: "date", label: d === new Date().toLocaleDateString() ? "Today" : d }); lastDate = d; } grouped.push({ type: "msg", msg: m }); });

  const sharedContactsProps = { filtContacts, peer, unread, totalUnread, search, setSearch, userId, onOpen: openChat };
  const sharedPanelProps    = { peer, grouped, userId, input, setInput, sending, replyingTo, setReplyingTo, handleKey, sendMessage, handleDelete, handleEdit, endRef, inputRef };

  return (
    <>
      <div className="chat-desktop-layout" style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 0, background: "#fff", borderRadius: "1.5rem", boxShadow: "0 4px 20px -2px rgba(0,0,0,.07)", overflow: "hidden", minHeight: 600 }}>
        <div style={{ borderRight: "1px solid #f3f4f6" }}><ContactsSidebar {...sharedContactsProps} /></div>
        {!peer ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "1rem", background: "#fafafa", padding: "2rem" }}>
            <div style={{ width: 72, height: 72, borderRadius: "20px", background: "linear-gradient(135deg,#eff6ff,#dbeafe)", display: "flex", alignItems: "center", justifyContent: "center" }}><span className="ms" style={{ color: "#3b82f6", fontSize: "2rem" }}>chat_bubble</span></div>
            <h3 style={{ fontWeight: 700, color: "#111827", margin: 0, fontSize: "1.125rem" }}>Hospital Chat</h3>
            <p style={{ color: "#6b7280", fontSize: ".875rem", textAlign: "center", maxWidth: 280, margin: 0 }}>Select a colleague to start chatting.</p>
          </div>
        ) : (
          <ChatMessagePanel {...sharedPanelProps} onBack={() => { setPeer(null); setReplyingTo(null); }} />
        )}
      </div>
      <div className="chat-mobile-layout" style={{ display: "none", flexDirection: "column" }}>
        {mobileView === "contacts" ? (
          <div className="card" style={{ overflow: "hidden" }}><ContactsSidebar {...sharedContactsProps} /></div>
        ) : peer ? (
          <div className="card" style={{ overflow: "hidden", display: "flex", flexDirection: "column", minHeight: "70vh" }}>
            <ChatMessagePanel {...sharedPanelProps} onBack={goBackToContacts} />
          </div>
        ) : null}
      </div>
    </>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// TREAT MODAL
// ════════════════════════════════════════════════════════════════════════════
const TREAT_STEPS = ["Patient", "Alert", "Note & Send"];

function TreatModal({ patients, userId, onClose, onSuccess, preselectedPatientId, preselectedAlertId }) {
  const [step,          setStep]          = useState(preselectedAlertId ? 2 : preselectedPatientId ? 1 : 0);
  const [selPatientId,  setSelPatientId]  = useState(preselectedPatientId || "");
  const [patientAlerts, setPatientAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [selAlertId,    setSelAlertId]    = useState(preselectedAlertId || "");
  const [alertVitals,   setAlertVitals]   = useState(null);
  const [vitalsLoading, setVitalsLoading] = useState(false);
  const [note,          setNote]          = useState("");
  const [submitting,    setSubmitting]    = useState(false);

  const selPatient = patients.find(p => String(p.patient_id) === String(selPatientId));
  const selAlert   = patientAlerts.find(a => String(a.id) === String(selAlertId));

  useEffect(() => {
    if (!selPatientId) return;
    setAlertsLoading(true);
    fetch(`${API}/alerts/for-patient/${selPatientId}`).then(r=>r.json()).then(d=>{setPatientAlerts(Array.isArray(d)?d:[]);setAlertsLoading(false);}).catch(()=>{setPatientAlerts([]);setAlertsLoading(false);});
  }, [selPatientId]);

  useEffect(() => {
    if (preselectedAlertId && preselectedPatientId) {
      setAlertsLoading(true);
      fetch(`${API}/alerts/for-patient/${preselectedPatientId}`).then(r=>r.json()).then(d=>{setPatientAlerts(Array.isArray(d)?d:[]);setAlertsLoading(false);}).catch(()=>{setPatientAlerts([]);setAlertsLoading(false);});
    }
  }, []);

  useEffect(() => {
    if (!selAlertId || !selPatientId) return;
    const alert = patientAlerts.find(a => String(a.id) === String(selAlertId));
    if (!alert) return;
    setVitalsLoading(true);
    fetch(`${API}/measurements/at-time?patientId=${selPatientId}&timestamp=${encodeURIComponent(alert.created_at)}`).then(r=>r.json()).then(d=>{setAlertVitals(d);setVitalsLoading(false);}).catch(()=>{setAlertVitals(null);setVitalsLoading(false);});
  }, [selAlertId, patientAlerts]);

  const obsIsThreat = THREAT_KEYWORDS_CLIENT.some(kw => note.toLowerCase().includes(kw.toLowerCase()));

  const handleSubmit = async () => {
    if (!note.trim()) return;
    setSubmitting(true);
    try {
      const vitals_snapshot = alertVitals ? {
        hr: alertVitals.hr, spo2: alertVitals.spo2,
        temp_body: alertVitals.temp_body, humidity: alertVitals.humidity,
        air_quality: alertVitals.air_quality, timestamp: alertVitals.created_at
      } : null;
      const r = await fetch(`${API}/medical-observations`, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ user_id:userId, patient_id:selPatientId, note, vitals_snapshot, alert_id:selAlertId||null }) });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || "Failed");
      if (data.emailPayload) {
        const { to_email, name, nameP, message } = data.emailPayload;
        if (to_email) { await emailjs.send(EMAILJS_SERVICE, EMAILJS_TEMPLATE, { to_email, to_name:nameP, name, nameP, message }, EMAILJS_KEY); await fetch(`${API}/medical-observations/${data.id}/email-sent`, { method:"PUT" }); }
      }
      onSuccess(data);
    } catch (err) { console.error(err); alert("Failed to save: " + err.message); }
    finally { setSubmitting(false); }
  };

  const canNext = [() => !!selPatientId, () => !!selAlertId, () => note.trim().length > 0];

  const TREAT_VITALS = [
    { k:"hr",          l:"Heart Rate",  u:"bpm", c:"#ef4444" },
    { k:"spo2",        l:"SpO₂",        u:"%",   c:"#3b82f6" },
    { k:"temp_body",   l:"Body Temp",   u:"°C",  c:"#f59e0b" },
    { k:"humidity",    l:"Humidity",    u:"%",   c:"#06b6d4" },
    { k:"air_quality", l:"Air Quality", u:"V",   c:"#10b981" },
  ];

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="card modal-box slide-up" style={{ width: "100%", maxWidth: 600, padding: 0, borderRadius: "1.75rem" }}>
        {/* Sticky header */}
        <div style={{ padding:"1.75rem 2rem 1.25rem", borderBottom:"1px solid #f3f4f6", position:"sticky", top:0, background:"#fff", zIndex:2, borderRadius:"1.75rem 1.75rem 0 0" }}>
          <div style={{ width: 40, height: 4, background: "#e5e7eb", borderRadius: 9999, margin: "0 auto .875rem" }} />
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"1.25rem" }}>
            <div>
              <h2 style={{ fontWeight:800, fontSize:"1.25rem", color:"#111827", margin:0 }}>
                <span className="ms" style={{ color:"#22c55e", fontSize:"1.125rem", verticalAlign:"middle", marginRight:".375rem" }}>medical_services</span>
                Treat Alert
              </h2>
              <p style={{ fontSize:".8125rem", color:"#9ca3af", margin:".2rem 0 0" }}>Link an observation to an alert</p>
            </div>
            <button onClick={onClose} style={{ width:36, height:36, borderRadius:"50%", background:"#f3f4f6", border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#6b7280", flexShrink:0 }}>
              <span className="ms" style={{ fontSize:"1rem" }}>close</span>
            </button>
          </div>
          {/* Step progress */}
          <div style={{ display:"flex", alignItems:"center", gap:0 }}>
            {TREAT_STEPS.map((label,i) => (
              <React.Fragment key={i}>
                <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:".25rem", flex:1 }}>
                  <div style={{ width:32, height:32, borderRadius:"50%", background:i<step?"#22c55e":i===step?"#3b82f6":"#f3f4f6", color:i<=step?"#fff":"#9ca3af", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700, fontSize:".8125rem", transition:"all .25s", boxShadow:i===step?"0 0 0 4px rgba(59,130,246,.2)":"none" }}>
                    {i<step?<span className="ms" style={{ fontSize:".875rem" }}>check</span>:i+1}
                  </div>
                  <span style={{ fontSize:".65rem", fontWeight:600, color:i<=step?"#111827":"#9ca3af", whiteSpace:"nowrap" }}>{label}</span>
                </div>
                {i<TREAT_STEPS.length-1&&<div style={{ flex:2, height:2, background:i<step?"#22c55e":"#f3f4f6", borderRadius:"9999px", marginBottom:"1.25rem", transition:"background .25s" }}/>}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step content */}
        <div style={{ padding:"1.75rem 2rem" }}>
          {step===0&&(
            <div>
              <p style={{ fontSize:".875rem", fontWeight:600, color:"#374151", marginBottom:"1rem" }}>Select the patient:</p>
              <div style={{ display:"flex", flexDirection:"column", gap:".625rem" }}>
                {patients.map(p => {
                  const isSel = String(p.patient_id) === String(selPatientId);
                  return (
                    <div key={p.patient_id} onClick={()=>setSelPatientId(String(p.patient_id))} style={{ display:"flex", alignItems:"center", gap:"1rem", padding:"1rem 1.25rem", borderRadius:"1rem", border:isSel?"2px solid #3b82f6":"1.5px solid #e5e7eb", cursor:"pointer", background:isSel?"#eff6ff":"#fff", transition:"all .15s" }}>
                      <div style={{ width:40, height:40, borderRadius:"50%", background:isSel?"#3b82f6":"#f3f4f6", color:isSel?"#fff":"#6b7280", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700, fontSize:"1.125rem", flexShrink:0 }}>{p.name.charAt(0)}</div>
                      <div style={{ flex:1 }}><p style={{ fontWeight:700, color:"#111827", margin:0 }}>{p.name}</p><p style={{ fontSize:".75rem", color:"#9ca3af", margin:0 }}>DOB: {fmt(p.dob)}</p></div>
                      {isSel&&<span className="ms" style={{ color:"#3b82f6", fontSize:"1.25rem" }}>check_circle</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {step===1&&(
            <div>
              <p style={{ fontSize:".875rem", fontWeight:600, color:"#374151", marginBottom:"1rem" }}>Select the alert for <strong>{selPatient?.name}</strong>:</p>
              {alertsLoading ? (
                <div style={{ display:"flex", alignItems:"center", gap:".75rem", padding:"2rem", justifyContent:"center" }}>
                  <div className="spin" style={{ width:24, height:24, border:"3px solid #dbeafe", borderTopColor:"#3b82f6", borderRadius:"50%" }}/>
                  <span style={{ color:"#9ca3af" }}>Loading alerts…</span>
                </div>
              ) : patientAlerts.length===0 ? (
                <div style={{ textAlign:"center", padding:"3rem 1rem", background:"#f9fafb", borderRadius:"1rem" }}>
                  <p style={{ color:"#9ca3af", margin:0 }}>No alerts found for this patient.</p>
                </div>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:".625rem", maxHeight:300, overflowY:"auto" }}>
                  {patientAlerts.map(a => {
                    const isSel = String(a.id) === String(selAlertId);
                    const cfg   = VC[a.type];
                    return (
                      <div key={a.id} onClick={() => setSelAlertId(String(a.id))}
                        style={{
                          display: "flex", alignItems: "center", gap: "1rem",
                          padding: "1rem 1.25rem", borderRadius: "1rem",
                          border: isSel ? "2px solid #3b82f6" : "1.5px solid #e5e7eb",
                          cursor: "pointer",
                          background: isSel ? "#eff6ff" : a.status === "treated" ? "#f0fdf4" : "#fff",
                          transition: "all .15s",
                        }}>
                        <div style={{ width:40, height:40, borderRadius:"12px", background:isSel?"#3b82f6":(cfg?.bg||"#f3f4f6"), display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                          <span className="ms" style={{ color:isSel?"#fff":(cfg?.color||"#6b7280"), fontSize:"1.125rem" }}>{cfg?.icon||"warning"}</span>
                        </div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:".5rem", marginBottom:".2rem", flexWrap:"wrap" }}>
                            <span style={{ fontWeight:700, fontSize:".875rem", color:"#111827" }}>{cfg?.label||a.type}</span>
                            <span style={{ fontWeight:700, fontSize:".875rem", color:cfg?.color||"#374151" }}>
                              {a.type==="air_quality"?(+a.value).toFixed(0):fmtN(a.value)}
                              <span style={{ fontSize:".75rem", color:"#9ca3af", fontWeight:400 }}> {cfg?.unit||""}</span>
                            </span>
                            <span style={{ background:AS[a.status]?.bg||"#f3f4f6", color:AS[a.status]?.c||"#6b7280", fontSize:".65rem", fontWeight:700, padding:"1px 7px", borderRadius:"9999px" }}>
                              {AS[a.status]?.label||a.status}
                            </span>
                            {/* Doctor-treated badge — still selectable */}
                            {a.status==="treated" && (
                              <span style={{ background:"#f0fdf4", color:"#166534", fontSize:".65rem", fontWeight:700, padding:"1px 7px", borderRadius:"9999px", display:"inline-flex", alignItems:"center", gap:".25rem" }}>
                                <span className="ms" style={{ fontSize:".7rem" }}>medical_services</span>
                                Treated — you can still observe
                              </span>
                            )}
                          </div>
                          <p style={{ fontSize:".75rem", color:"#9ca3af", margin:0 }}>{fmtT(a.created_at)} · {timeAgo(a.created_at)}</p>
                        </div>
                        {isSel&&<span className="ms" style={{ color:"#3b82f6", fontSize:"1.25rem", flexShrink:0 }}>check_circle</span>}
                      </div>
                    );
                  })}
                </div>
              )}
              <button onClick={()=>{setSelAlertId("");setStep(2);}} style={{ marginTop:"1rem", width:"100%", padding:".625rem", background:"none", border:"1.5px dashed #d1d5db", borderRadius:".875rem", color:"#9ca3af", fontSize:".8125rem", fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>
                Skip — write observation without linking an alert
              </button>
            </div>
          )}

          {step===2&&(
            <div>
              <div style={{ display:"flex", gap:".625rem", marginBottom:"1.25rem", flexWrap:"wrap" }}>
                <div style={{ background:"#eff6ff", borderRadius:".875rem", padding:".625rem 1rem", display:"flex", alignItems:"center", gap:".5rem" }}>
                  <div style={{ width:28, height:28, borderRadius:"50%", background:"#3b82f6", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700, fontSize:".875rem" }}>{selPatient?.name?.charAt(0)}</div>
                  <span style={{ fontSize:".8125rem", fontWeight:700, color:"#1e40af" }}>{selPatient?.name}</span>
                </div>
                {selAlert ? (
                  <div style={{ background:AS[selAlert.status]?.bg||"#f9fafb", borderRadius:".875rem", padding:".625rem 1rem", display:"flex", alignItems:"center", gap:".5rem" }}>
                    <span className="ms" style={{ color:VC[selAlert.type]?.color||"#6b7280", fontSize:"1rem" }}>{VC[selAlert.type]?.icon||"warning"}</span>
                    <span style={{ fontSize:".8125rem", fontWeight:700, color:"#374151" }}>{VC[selAlert.type]?.label||selAlert.type}: {selAlert.type==="air_quality"?(+selAlert.value).toFixed(0):fmtN(selAlert.value)}</span>
                  </div>
                ) : (
                  <div style={{ background:"#f9fafb", borderRadius:".875rem", padding:".625rem 1rem" }}>
                    <span style={{ fontSize:".8125rem", color:"#9ca3af" }}>No alert linked</span>
                  </div>
                )}
              </div>

              {selAlert && (
                <div style={{ background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:"1rem", padding:"1rem", marginBottom:"1.25rem" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:".5rem", marginBottom:".75rem" }}>
                    <span className="ms" style={{ color:"#3b82f6", fontSize:"1rem" }}>history</span>
                    <span style={{ fontSize:".75rem", fontWeight:700, color:"#1e40af", textTransform:"uppercase", letterSpacing:".05em" }}>Vitals at alert time · {fmtT(selAlert.created_at)}</span>
                    {vitalsLoading && <div className="spin" style={{ width:14, height:14, border:"2px solid #dbeafe", borderTopColor:"#3b82f6", borderRadius:"50%", marginLeft:"auto" }}/>}
                  </div>
                  {vitalsLoading ? (
                    <p style={{ color:"#9ca3af", fontSize:".8125rem", margin:0 }}>Loading vitals…</p>
                  ) : alertVitals ? (
                    <div style={{ display:"flex", flexWrap:"wrap", gap:".5rem" }}>
                      {TREAT_VITALS.filter(m=>alertVitals[m.k]!=null).map(m=>{
                        const st=vStat(m.k,alertVitals[m.k]);
                        return (
                          <div key={m.k} style={{ background:"#fff", border:`1px solid ${st==="Abnormal"?"#fecaca":st==="Watch"?"#fde68a":"#e5e7eb"}`, borderRadius:".75rem", padding:".5rem .875rem", textAlign:"center", minWidth:80 }}>
                            <p style={{ fontSize:".65rem", color:"#9ca3af", margin:0, fontWeight:600, textTransform:"uppercase" }}>{m.l}</p>
                            <p style={{ fontSize:"1.125rem", fontWeight:700, color:m.c, margin:"2px 0" }}>{m.k==="air_quality"?(+alertVitals[m.k]).toFixed(0):(+alertVitals[m.k]).toFixed(1)}<span style={{ fontSize:".65rem", color:"#9ca3af", fontWeight:400 }}>{m.u}</span></p>
                            <span style={{ background:SS[st].bg, color:SS[st].c, fontSize:".6rem", fontWeight:700, padding:"1px 6px", borderRadius:"9999px" }}>{st}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p style={{ color:"#9ca3af", fontSize:".8125rem", margin:0 }}>No measurement records found near this alert's timestamp.</p>
                  )}
                </div>
              )}

              <div style={{ marginBottom:"1rem" }}>
                <label style={{ display:"block", fontSize:".75rem", fontWeight:700, color:"#9ca3af", textTransform:"uppercase", letterSpacing:".05em", marginBottom:".5rem" }}>Clinical Note *</label>
                <textarea rows={6} value={note} onChange={e=>setNote(e.target.value)} placeholder="Write your clinical observation here…"
                  style={{ width:"100%", boxSizing:"border-box", border:"1.5px solid #e5e7eb", borderRadius:".875rem", padding:".75rem 1rem", fontSize:".875rem", outline:"none", fontFamily:"inherit", resize:"none", lineHeight:1.6 }}/>
                {note && obsIsThreat && (
                  <div style={{ marginTop:".625rem", background:"#fef2f2", border:"1px solid #fecaca", borderRadius:".75rem", padding:".625rem .875rem", display:"flex", alignItems:"center", gap:".5rem" }}>
                    <span className="ms" style={{ color:"#ef4444" }}>warning</span>
                    <div>
                      <p style={{ fontSize:".75rem", fontWeight:700, color:"#991b1b", margin:0 }}>⚠️ Threat detected</p>
                      <p style={{ fontSize:".7rem", color:"#b91c1c", margin:0 }}>Urgent alert will be emailed with vitals snapshot</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sticky footer */}
        <div style={{ padding:"1.25rem 2rem 1.75rem", borderTop:"1px solid #f3f4f6", display:"flex", gap:".75rem", position:"sticky", bottom:0, background:"#fff", borderRadius:"0 0 1.75rem 1.75rem" }}>
          {step > 0 && (
            <button onClick={()=>setStep(s=>s-1)} style={{ flex:1, padding:".75rem", background:"#f3f4f6", color:"#374151", border:"none", borderRadius:".875rem", fontWeight:600, fontSize:".9375rem", cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:".5rem" }}>
              <span className="ms" style={{ fontSize:"1rem" }}>arrow_back</span>Back
            </button>
          )}
          {step < 2 ? (
            <button onClick={()=>setStep(s=>s+1)} disabled={!canNext[step]()} style={{ flex:2, padding:".75rem", background:canNext[step]()?"#3b82f6":"#93c5fd", color:"#fff", border:"none", borderRadius:".875rem", fontWeight:700, fontSize:".9375rem", cursor:canNext[step]()?"pointer":"not-allowed", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:".5rem", boxShadow:canNext[step]()?"0 4px 14px rgba(59,130,246,.3)":"none" }}>
              Next <span className="ms" style={{ fontSize:"1rem" }}>arrow_forward</span>
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={submitting||!note.trim()} style={{ flex:2, padding:".75rem", background:note.trim()?"#3b82f6":"#93c5fd", color:"#fff", border:"none", borderRadius:".875rem", fontWeight:700, fontSize:".9375rem", cursor:note.trim()?"pointer":"not-allowed", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:".5rem", boxShadow:note.trim()?"0 4px 14px rgba(59,130,246,.3)":"none" }}>
              {submitting ? (
                <><div className="spin" style={{ width:16, height:16, border:"2px solid rgba(255,255,255,.4)", borderTopColor:"#fff", borderRadius:"50%" }}/>Saving…</>
              ) : (
                <><span className="ms" style={{ fontSize:"1rem" }}>send</span>Save &amp; Send Email</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════
export default function MedicalStuffDashboard() {
  const navigate = useNavigate();
  const userId   = localStorage.getItem("userId");

  const [me,            setMe]            = useState({ username: "User", role: "user", hospital_id: null });
  const [page,          setPage]          = useState("overview");
  const [patients,      setPatients]      = useState([]);
  const [measurements,  setMeasurements]  = useState([]);
  const [alerts,        setAlerts]        = useState([]);
  const [observations,  setObservations]  = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [devices,       setDevices]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [notifOpen,     setNotifOpen]     = useState(false);
  const [lastUpdate,    setLastUpdate]    = useState(new Date());
  const [liveBadge,     setLiveBadge]     = useState(false);
  const [emailSending,  setEmailSending]  = useState(false);

  const notifRef        = useRef(null);
  const refreshInterval = useRef(null);

  const [selPat,      setSelPat]      = useState(null);
  const [calPat,      setCalPat]      = useState(null);
  const [calYear,     setCalYear]     = useState(new Date().getFullYear());
  const [calMonth,    setCalMonth]    = useState(new Date().getMonth());
  const [deviceDays,  setDeviceDays]  = useState([]);
  const [alertFilter, setAlertFilter] = useState("all");
  const [obsText,     setObsText]     = useState("");
  const [obsPatId,    setObsPatId]    = useState("");
  const [viewObs,     setViewObs]     = useState(null);
  const [treatModal,  setTreatModal]  = useState(null);
  const [chatUnread,  setChatUnread]  = useState(0);

  const getPatientDeviceVitals = useCallback(patientId => {
    const device = devices.find(d => String(d.patient_id) === String(patientId));
    return device?.latest || null;
  }, [devices]);

  const patientIds = patients.map(p => p.patient_id);
  const { vitals: esp32Vitals } = useEsp32Vitals(patientIds);

  const getMergedVitals = useCallback(patientId => {
    return esp32Vitals[patientId] || getPatientDeviceVitals(patientId) || null;
  }, [esp32Vitals, getPatientDeviceVitals]);

  const fetchData = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
      const [uR,pR,mR,aR,oR,nR,dR] = await Promise.all([
        fetch(`${API}/subadmin-info?subAdminId=${userId}`),
        fetch(`${API}/user-patients?userId=${userId}`),
        fetch(`${API}/user-measurements?userId=${userId}`),
        fetch(`${API}/user-alerts?userId=${userId}`),
        fetch(`${API}/medical-observations?userId=${userId}`),
        fetch(`${API}/user-notifications?userId=${userId}`),
        fetch(`${API}/device-live?userId=${userId}`),
      ]);
      const [u,p,m,a,o,n,d] = await Promise.all([uR.json(),pR.json(),mR.json(),aR.json(),oR.json(),nR.json(),dR.json()]);
      setMe(u);
      const pts = Array.isArray(p)?p:[];
      setPatients(pts);
      setMeasurements(Array.isArray(m)?m:[]);
      setAlerts(Array.isArray(a)?a:[]);
      setObservations(Array.isArray(o)?o:[]);
      setNotifications(Array.isArray(n)?n:[]);
      setDevices(Array.isArray(d)?d:[]);
      setLastUpdate(new Date());
      setLiveBadge(true); setTimeout(()=>setLiveBadge(false),500);
      if (pts.length && !selPat) { setSelPat(pts[0]); setCalPat(pts[0]); }
    } catch(e) { console.error(e); }
    if (showLoading) setLoading(false);
  }, [userId, selPat]);

  const fetchChatUnread = useCallback(async () => {
    try { const r=await fetch(`${API}/chat/unread?userId=${userId}`); if(!r.ok)return; const data=await r.json(); const total=(Array.isArray(data)?data:[]).reduce((a,b)=>a+(+b.count),0); setChatUnread(total); } catch {}
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    fetchData(true); fetchChatUnread();
    const liveRef = setInterval(()=>{
      fetch(`${API}/device-live?userId=${userId}`).then(r=>r.json()).then(data=>{if(Array.isArray(data))setDevices(data);}).catch(()=>{});
    }, 5000);
    refreshInterval.current = setInterval(()=>{ fetchData(false); fetchChatUnread(); }, 5000);
    return ()=>{ clearInterval(refreshInterval.current); clearInterval(liveRef); };
  }, [userId, fetchData, fetchChatUnread]);

  useEffect(() => {
    if (!calPat) return;
    fetch(`${API}/device-days?patientId=${calPat.patient_id}&year=${calYear}&month=${calMonth+1}`).then(r=>r.json()).then(d=>setDeviceDays(Array.isArray(d)?d:[])).catch(()=>setDeviceDays([]));
  }, [calPat, calYear, calMonth, lastUpdate]);

  const measFor   = pid => measurements.filter(m => String(m.patient_id) === String(pid));
  const alertsFor = pid => alerts.filter(a => String(a.patient_id) === String(pid));

  const latestMeas = pid => {
    const rows = measFor(pid);
    if (!rows.length) return null;
    return rows.reduce((a,b) => new Date(a.created_at) > new Date(b.created_at) ? a : b);
  };

  const histFor = (pid, col) =>
    measFor(pid).sort((a,b)=>new Date(a.created_at)-new Date(b.created_at)).map(m=>m[col]).filter(v=>v!=null);

  const patStatus = pid => {
    const source = getMergedVitals(pid) || latestMeas(pid);
    if (!source) return "unknown";
    const statuses = Object.keys(VC).map(col => vStat(col, source[col]));
    if (statuses.includes("Abnormal")) return "Abnormal";
    if (statuses.includes("Watch"))    return "Watch";
    return "Normal";
  };

  const newAlerts    = alerts.filter(a => a.status === "new").length;
  const unreadNotifs = notifications.filter(n => n.status === "pending").length;

  const logout = async () => {
    if (refreshInterval.current) clearInterval(refreshInterval.current);
    const loginTime  = parseInt(localStorage.getItem("loginTime")||"0");
    const activeTime = loginTime ? Math.floor((Date.now()-loginTime)/1000) : null;
    try { await fetch(`${API}/logout`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({userId,activeTime})}); } catch(e){console.error(e);}
    ["userRole","userId","adminHospitalId","loginTime"].forEach(k=>localStorage.removeItem(k));
    navigate("/signin");
  };

  const updateAlert = async (id, status, comment="") => {
    await fetch(`${API}/alerts/${id}/status`,{method:"PUT",headers:{"Content-Type":"application/json"},body:JSON.stringify({status,comment,actorId:Number(userId)})});
    fetchData(false);
  };

  const markNotifSeen = async notificationId => {
    try {
      await fetch(`${API}/user-notifications/${notificationId}/status`,{method:"PUT",headers:{"Content-Type":"application/json"},body:JSON.stringify({status:"seen",actorId:Number(userId)})});
      fetchData(false);
    } catch(err){console.error(err);}
  };

  // ── Called when staff clicks "Send Observation to Patient" in a notification
  const handleNotifSendObs = async (notif) => {
    setNotifOpen(false);
    // Mark as seen first (don't block UI)
    if (notif.status === "pending") {
      fetch(`${API}/user-notifications/${notif.id}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "seen", actorId: Number(userId) }),
      }).catch(() => {});
    }
    // Navigate to observations page and open TreatModal pre-filled with patient + notifId
    setPage("observations");
    setTreatModal({ patientId: String(notif.patient_id), notifId: notif.id });
  };

  const markAllNotifsRead = async () => {
    await Promise.all(
      notifications.filter(n => n.status === "pending").map(n =>
        fetch(`${API}/user-notifications/${n.id}/status`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: "seen", actorId: Number(userId) }),
        })
      )
    );
    setNotifications(prev => prev.map(n => n.status === "pending" ? { ...n, status: "seen" } : n));
  };

  const addObs = async () => {
    if (!obsText.trim() || !obsPatId) return alert("Please select a patient and write an observation.");
    const deviceVitals    = getMergedVitals(obsPatId);
    const lastM           = latestMeas(obsPatId);
    const vitals_snapshot = deviceVitals || (lastM ? {
      hr: lastM.hr, spo2: lastM.spo2, temp_body: lastM.temp_body,
      humidity: lastM.humidity, air_quality: lastM.air_quality,
      timestamp: new Date().toISOString()
    } : null);
    const r = await fetch(`${API}/medical-observations`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({user_id:userId,patient_id:obsPatId,note:obsText,vitals_snapshot})});
    const data = await r.json();
    if (!r.ok) return alert("Failed to save observation.");
    if (data.emailPayload) {
      const { to_email, name, nameP, message } = data.emailPayload;
      if (!to_email) { alert("Observation saved but patient has no email."); setObsText(""); setObsPatId(""); fetchData(false); return; }
      setEmailSending(true);
      try {
        const result = await emailjs.send(EMAILJS_SERVICE, EMAILJS_TEMPLATE, {to_email,to_name:nameP,name,nameP,message}, EMAILJS_KEY);
        if (result.status===200) { alert(`${data.is_threat?"⚠️ Threat alert":"Observation"} sent to ${nameP} at ${to_email}`); await fetch(`${API}/medical-observations/${data.id}/email-sent`,{method:"PUT"}); }
      } catch(emailErr) { alert(`Observation saved but email failed.\n${emailErr?.text||JSON.stringify(emailErr)}`); }
      finally { setEmailSending(false); }
    }
    setObsText(""); setObsPatId(""); fetchData(false);
  };

  const deleteObs = async id => {
    if (!window.confirm("Delete this observation?")) return;
    await fetch(`${API}/medical-observations/${id}`,{method:"DELETE",headers:{"Content-Type":"application/json"},body:JSON.stringify({actorId:Number(userId)})});
    fetchData(false);
  };

  const obsIsThreat = THREAT_KEYWORDS_CLIENT.some(kw => obsText.toLowerCase().includes(kw.toLowerCase()));

  const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const DAYS   = ["SUN","MON","TUE","WED","THU","FRI","SAT"];
  const dim    = (y,m) => new Date(y,m+1,0).getDate();
  const fdm    = (y,m) => new Date(y,m,1).getDay();
  const isDev  = day => {
    const ds = `${calYear}-${String(calMonth+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
    return deviceDays.includes(ds);
  };

  const NB = {
    pending: { bg:"#fef2f2", c:"#991b1b", dot:"#ef4444", label:"New"  },
    seen:    { bg:"#fffbeb", c:"#b45309", dot:"#f59e0b", label:"Seen" },
    done:    { bg:"#f0fdf4", c:"#166534", dot:"#22c55e", label:"Done" },
  };

  const navItems = [
    { id:"overview",     label:"Overview",     short:"Home",   icon:"dashboard"                                          },
    { id:"alerts",       label:"Alerts",       short:"Alerts", icon:"notification_important", badge: newAlerts           },
    { id:"vitals",       label:"Vital Signs",  short:"Vitals", icon:"monitor_heart"                                      },
    { id:"calendar",     label:"Calendar",     short:"Cal",    icon:"calendar_month"                                     },
    { id:"observations", label:"Observations", short:"Obs",    icon:"edit_note"                                          },
    { id:"chatbot",      label:"AI Bot",       short:"AI",     icon:"smart_toy"                                          },
    { id:"chat",         label:"Chat",         short:"Chat",   icon:"chat_bubble",            badge: chatUnread          },
  ];

  useEffect(() => {
    const handler = e => { if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const TABLE_VITALS = [
    { k:"hr",          l:"HR (bpm)"        },
    { k:"spo2",        l:"SpO₂ (%)"        },
    { k:"temp_body",   l:"Body Temp (°C)"  },
    { k:"humidity",    l:"Humidity (%)"    },
    { k:"air_quality", l:"Air Quality (ppm)" },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "#f3f4f6", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
      <style>{MOBILE_STYLES}</style>

      {/* ══ MOBILE HEADER ══ */}
      <div className="msd-mobile-header">
        <div style={{ display: "flex", alignItems: "center", gap: ".75rem" }}>
          <div style={{ width: 32, height: 32, borderRadius: "10px", background: "#3b82f6", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span className="ms" style={{ color: "#fff", fontSize: ".9rem" }}>local_hospital</span>
          </div>
          <div>
            <p style={{ fontWeight: 800, fontSize: ".875rem", color: "#111827", margin: 0 }}>{me.username}</p>
            <p style={{ fontSize: ".6rem", color: "#9ca3af", margin: 0, textTransform: "capitalize" }}>CareTech · {me.role}</p>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: ".3rem" }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: liveBadge ? "#22c55e" : "#3b82f6", transition: "all .3s" }} className="pls" />
            <span style={{ fontSize: ".6rem", color: "#9ca3af" }}>Live</span>
          </div>
          {devices.length > 0 && (
            <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "9999px", padding: ".2rem .5rem", display: "flex", alignItems: "center", gap: ".25rem" }}>
              <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#22c55e" }} className="pls" />
              <span style={{ fontSize: ".6rem", fontWeight: 700, color: "#166534" }}>{devices.length}</span>
            </div>
          )}

          {/* ── MOBILE Notification bell ── */}
          <div ref={notifRef} style={{ position: "relative" }}>
            <button onClick={() => setNotifOpen(o => !o)}
              style={{ width: 36, height: 36, borderRadius: "50%", background: notifOpen ? "#eff6ff" : "#f3f4f6", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280", position: "relative" }}>
              <span className="ms" style={{ fontSize: "1.125rem" }}>notifications</span>
              {unreadNotifs > 0 && (
                <span style={{ position: "absolute", top: 4, right: 4, width: 14, height: 14, background: "#ef4444", borderRadius: "50%", border: "2px solid #fff", fontSize: ".55rem", fontWeight: 700, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {unreadNotifs > 9 ? "9+" : unreadNotifs}
                </span>
              )}
            </button>
            {notifOpen && (
              <div className="card" style={{ position: "absolute", top: "calc(100% + .5rem)", right: 0, width: "calc(100vw - 2rem)", maxWidth: 360, maxHeight: 520, overflowY: "auto", zIndex: 100, padding: "1rem", boxShadow: "0 20px 60px -8px rgba(0,0,0,.18)" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: ".875rem" }}>
                  <h3 style={{ fontWeight: 700, color: "#111827", margin: 0, fontSize: ".9375rem" }}>
                    Notifications <span style={{ color: "#9ca3af", fontWeight: 400, fontSize: ".8rem" }}>({unreadNotifs} unread)</span>
                  </h3>
                  {unreadNotifs > 0 && (
                    <button onClick={markAllNotifsRead} style={{ background: "none", border: "none", color: "#3b82f6", fontSize: ".75rem", fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                      Mark all
                    </button>
                  )}
                </div>
                {notifications.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "2rem 1rem" }}>
                    <span className="ms" style={{ fontSize: "2rem", color: "#e5e7eb", display: "block", marginBottom: ".5rem" }}>notifications_none</span>
                    <p style={{ color: "#9ca3af", fontSize: ".875rem", margin: 0 }}>No notifications</p>
                  </div>
                ) : notifications.slice(0, 10).map((n, idx) => {
                  const nb = NB[n.status] ?? NB.seen;
                  return (
                    <NotifCard
                      key={n.id}
                      n={n}
                      idx={idx}
                      nb={nb}
                      onMarkSeen={markNotifSeen}
                      onSendObservation={handleNotifSendObs}
                      compact={true}
                    />
                  );
                })}
              </div>
            )}
          </div>

          {chatUnread > 0 && (
            <button onClick={() => setPage("chat")} style={{ background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: "9999px", padding: ".3rem .75rem", color: "#6d28d9", fontSize: ".7rem", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: ".3rem" }}>
              <span className="ms" style={{ fontSize: ".85rem" }}>chat_bubble</span>{chatUnread}
            </button>
          )}
        </div>
      </div>

      {/* ══ DESKTOP HEADER ══ */}
      <div className="msd-desktop-header" style={{ background: "#fff", borderBottom: "1px solid #f0f0f0", padding: "1rem 2rem", position: "sticky", top: 0, zIndex: 40 }}>
        <div style={{ maxWidth: 1600, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: ".5rem", flexShrink: 0 }}>
            <span style={{ fontSize: "1.5rem", fontWeight: 700, color: "#111827", letterSpacing: "-.02em" }}>CareTech</span>
            <div style={{ display: "flex", alignItems: "center", gap: ".5rem", marginLeft: "1rem" }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: liveBadge ? "#22c55e" : "#3b82f6", boxShadow: liveBadge ? "0 0 8px #22c55e" : "none", transition: "all .3s" }} />
              <span style={{ fontSize: ".75rem", color: "#6b7280" }}>Live · {lastUpdate.toLocaleTimeString()}</span>
            </div>
          </div>

          <nav className="msd-desktop-nav" style={{ alignItems: "center", background: "#fff", borderRadius: "9999px", padding: ".375rem .5rem", boxShadow: "0 2px 12px rgba(0,0,0,.07)", gap: "2px", overflowX: "auto" }}>
            {navItems.map(n => (
              <button key={n.id} onClick={() => setPage(n.id)} className={`pill ${page === n.id ? "pact" : "pinact"}`}>
                <span className="ms" style={{ fontSize: "1rem" }}>{n.icon}</span>
                {n.label}
                {n.badge > 0 && (
                  <span style={{ background: page === n.id ? "#fff" : n.id === "chat" ? "#8b5cf6" : "#ef4444", color: page === n.id ? (n.id === "chat" ? "#8b5cf6" : "#3b82f6") : "#fff", fontSize: ".65rem", fontWeight: 700, padding: "1px 6px", borderRadius: "9999px" }}>
                    {n.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>

          <div style={{ display: "flex", alignItems: "center", gap: ".75rem", flexShrink: 0 }}>
            {/* ── DESKTOP Notification bell ── */}
            <div ref={notifRef} style={{ position: "relative" }}>
              <button onClick={() => setNotifOpen(o => !o)}
                style={{ width: 40, height: 40, borderRadius: "50%", background: notifOpen ? "#eff6ff" : "#fff", border: notifOpen ? "2px solid #bfdbfe" : "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: notifOpen ? "#3b82f6" : "#6b7280", boxShadow: "0 2px 8px rgba(0,0,0,.07)", position: "relative", transition: "all .15s" }}>
                <span className="ms" style={{ fontSize: "1.125rem" }}>notifications</span>
                {unreadNotifs > 0 && (
                  <span style={{ position: "absolute", top: 6, right: 6, width: 16, height: 16, background: "#ef4444", borderRadius: "50%", border: "2px solid #fff", fontSize: ".6rem", fontWeight: 700, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>
                    {unreadNotifs > 9 ? "9+" : unreadNotifs}
                  </span>
                )}
              </button>
              {notifOpen && (
                <div className="card" style={{ position: "absolute", top: "calc(100% + .75rem)", right: 0, width: 440, maxHeight: 580, overflowY: "auto", zIndex: 100, padding: "1.25rem", boxShadow: "0 20px 60px -8px rgba(0,0,0,.18)" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
                    <div>
                      <h3 style={{ fontWeight: 700, color: "#111827", margin: 0, fontSize: "1rem" }}>Notifications</h3>
                      <p style={{ fontSize: ".75rem", color: "#9ca3af", margin: ".125rem 0 0" }}>{unreadNotifs} unread</p>
                    </div>
                    {unreadNotifs > 0 && (
                      <button onClick={markAllNotifsRead} style={{ background: "none", border: "none", color: "#3b82f6", fontSize: ".8125rem", fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                        Mark all seen
                      </button>
                    )}
                  </div>
                  {notifications.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "2.5rem 1rem" }}>
                      <span className="ms" style={{ fontSize: "2.5rem", color: "#e5e7eb", display: "block", marginBottom: ".5rem" }}>notifications_none</span>
                      <p style={{ color: "#9ca3af", fontSize: ".875rem", margin: 0 }}>No notifications yet</p>
                    </div>
                  ) : notifications.map((n, idx) => {
                    const nb = NB[n.status] ?? NB.seen;
                    return (
                      <NotifCard
                        key={n.id}
                        n={n}
                        idx={idx}
                        nb={nb}
                        onMarkSeen={markNotifSeen}
                        onSendObservation={handleNotifSendObs}
                        compact={false}
                      />
                    );
                  })}
                </div>
              )}
            </div>

            <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#3b82f6", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, boxShadow: "0 2px 8px rgba(59,130,246,.4)" }}>{(me.username||"U").charAt(0).toUpperCase()}</div>
            <div style={{ lineHeight: 1.3 }}><p style={{ fontWeight: 700, color: "#111827", margin: 0, fontSize: ".875rem" }}>{me.username}</p><p style={{ color: "#9ca3af", margin: 0, fontSize: ".75rem", textTransform: "capitalize" }}>{me.role}</p></div>
            <button onClick={logout} style={{ width: 40, height: 40, borderRadius: "50%", background: "#fff", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", boxShadow: "0 2px 8px rgba(0,0,0,.07)" }}><span className="ms" style={{ fontSize: "1.125rem" }}>logout</span></button>
          </div>
        </div>
      </div>

      {/* ══ MAIN CONTENT ══ */}
      <div className="msd-main-pad fade-in">
        <div className="msd-page-content">

          {loading && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", gap: "1rem" }}>
              <div className="spin" style={{ width: 48, height: 48, border: "4px solid #dbeafe", borderTopColor: "#3b82f6", borderRadius: "50%" }} />
              <p style={{ color: "#9ca3af", fontWeight: 600 }}>Loading…</p>
            </div>
          )}

          {!loading && (<>

            {/* ═══════════ OVERVIEW ═══════════ */}
            {page === "overview" && (
              <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem", marginBottom: "3rem" }}>
                <div className="ov-two-col" style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: "1.5rem", alignItems: "start" }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
                    <div>
                      <h1 style={{ fontSize: "1.875rem", fontWeight: 700, color: "#111827", margin: 0 }}>Dashboard</h1>
                      <p style={{ color: "#6b7280", margin: ".25rem 0 0", fontSize: ".875rem" }}>Patient health overview and real-time vitals from connected devices.</p>
                    </div>
                    <LiveDevices devices={devices} patients={patients} selectedPatientId={selPat?.patient_id} onSelectPatient={setSelPat} />

                    <div className="ov-stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: "1rem" }}>
                      {[
                        { label:"My Patients",  val:patients.length,      icon:"folder_shared",          bg:"#eff6ff", c:"#2563eb" },
                        { label:"New Alerts",   val:newAlerts,             icon:"notification_important", bg:"#fef2f2", c:"#dc2626" },
                        { label:"Observations", val:observations.length,   icon:"edit_note",              bg:"#f5f3ff", c:"#7c3aed" },
                      ].map(s => (
                        <div key={s.label} className="card hovcard" style={{ padding: "1.25rem" }}>
                          <div style={{ width:40, height:40, borderRadius:"12px", background:s.bg, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:".875rem" }}><span className="ms" style={{ color:s.c, fontSize:"1.125rem" }}>{s.icon}</span></div>
                          <p style={{ fontSize:"1.875rem", fontWeight:800, color:"#111827", margin:"0 0 .125rem" }}>{s.val}</p>
                          <p style={{ fontSize:".75rem", color:"#9ca3af", fontWeight:600, margin:0, textTransform:"uppercase", letterSpacing:".04em" }}>{s.label}</p>
                        </div>
                      ))}
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                      <div onClick={()=>setPage("chatbot")} className="card hovcard" style={{ padding:"1.25rem", cursor:"pointer", background:"linear-gradient(135deg,#eff6ff,#ecfeff)", border:"1px solid #bfdbfe" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:".75rem", marginBottom:".75rem" }}><div style={{ width:40, height:40, borderRadius:"12px", background:"linear-gradient(135deg,#3b82f6,#06b6d4)", display:"flex", alignItems:"center", justifyContent:"center" }}><span className="ms" style={{ color:"#fff", fontSize:"1.25rem" }}>smart_toy</span></div><div><p style={{ fontWeight:700, color:"#1e40af", margin:0, fontSize:".875rem" }}>AI Assistant</p><p style={{ fontSize:".7rem", color:"#6b7280", margin:0 }}>Meditron-powered</p></div></div>
                        <p style={{ fontSize:".8125rem", color:"#374151", margin:0 }}>Predict vitals, detect diseases.</p>
                      </div>
                      <div onClick={()=>setPage("chat")} className="card hovcard" style={{ padding:"1.25rem", cursor:"pointer", background:"linear-gradient(135deg,#f5f3ff,#ede9fe)", border:"1px solid #ddd6fe" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:".75rem", marginBottom:".75rem" }}><div style={{ width:40, height:40, borderRadius:"12px", background:"linear-gradient(135deg,#8b5cf6,#6d28d9)", display:"flex", alignItems:"center", justifyContent:"center" }}><span className="ms" style={{ color:"#fff", fontSize:"1.25rem" }}>chat_bubble</span></div><div><p style={{ fontWeight:700, color:"#5b21b6", margin:0, fontSize:".875rem" }}>Hospital Chat</p><p style={{ fontSize:".7rem", color:"#6b7280", margin:0 }}>Message colleagues{chatUnread>0&&<span style={{ background:"#ef4444", color:"#fff", fontSize:".6rem", fontWeight:700, padding:"1px 6px", borderRadius:"9999px", marginLeft:".375rem" }}>{chatUnread}</span>}</p></div></div>
                        <p style={{ fontSize:".8125rem", color:"#374151", margin:0 }}>Reply, edit & delete messages.</p>
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: ".625rem", flexWrap: "wrap", overflowX: "auto", paddingBottom: ".25rem" }}>
                      {patients.length === 0 && <p style={{ color: "#9ca3af", fontSize: ".875rem" }}>No patients assigned yet.</p>}
                      {patients.map(p => {
                        const st = patStatus(p.patient_id); const ss = SS[st];
                        const hasDevice = devices.some(d=>String(d.patient_id)===String(p.patient_id)) || !!esp32Vitals[p.patient_id];
                        return (
                          <button key={p.patient_id} onClick={()=>setSelPat(p)} className={`pill ${selPat?.patient_id===p.patient_id?"pact":"pinact"}`} style={{ background:selPat?.patient_id===p.patient_id?"#3b82f6":"#fff", boxShadow:"0 2px 8px rgba(0,0,0,.07)", whiteSpace:"nowrap" }}>
                            <span style={{ width:8, height:8, borderRadius:"50%", background:selPat?.patient_id===p.patient_id?"#fff":ss.dot }} />
                            {p.name}
                            {hasDevice && <span style={{ fontSize:".6rem", marginLeft:".25rem", color:selPat?.patient_id===p.patient_id?"#fff":"#22c55e" }}>●</span>}
                          </button>
                        );
                      })}
                    </div>

                    {selPat && (() => {
                      const deviceVitals  = getMergedVitals(selPat.patient_id);
                      const st            = patStatus(selPat.patient_id);
                      const ss            = SS[st];
                      const hasLiveDevice = deviceVitals !== null;
                      return (<>
                        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:"1rem" }}>
                          <div style={{ background:"#3b82f6", borderRadius:"1.5rem", padding:"1.5rem", color:"#fff", position:"relative", overflow:"hidden", minHeight:160, display:"flex", flexDirection:"column", justifyContent:"space-between" }}>
                            <div style={{ position:"absolute", bottom:0, right:0, width:"5rem", height:"6rem", background:"rgba(255,255,255,.12)", borderRadius:"50% 0 0 0" }}/>
                            <div>
                              <h3 style={{ fontWeight:700, fontSize:"1.125rem", margin:0 }}>{selPat.name}</h3>
                              <p style={{ color:"rgba(255,255,255,.75)", fontSize:".8125rem", margin:".375rem 0 0" }}>DOB: {fmt(selPat.dob)}</p>
                              <div style={{ marginTop:".75rem", background:"rgba(255,255,255,.2)", display:"inline-block", padding:".25rem .875rem", borderRadius:"9999px", fontSize:".75rem", fontWeight:600 }}>
                                {hasLiveDevice ? "📡 Live Connected" : "📋 Historical Data"}
                              </div>
                            </div>
                            <span style={{ background:ss.bg, color:ss.c, display:"inline-flex", alignItems:"center", gap:".375rem", padding:".3rem .875rem", borderRadius:"9999px", fontSize:".8125rem", fontWeight:700, width:"fit-content" }}>
                              <span style={{ width:7, height:7, borderRadius:"50%", background:ss.dot }}/>{st}
                            </span>
                          </div>
                          <div className="card" style={{ padding:"1.25rem", minHeight:160, display:"flex", flexDirection:"column", justifyContent:"space-between" }}>
                            <div style={{ display:"flex", alignItems:"center", gap:".5rem", flexWrap:"wrap" }}>
                              <div style={{ padding:".375rem", background:VC.hr.bg, borderRadius:"50%", flexShrink:0 }}><span className="ms" style={{ color:VC.hr.color, fontSize:".875rem" }}>favorite</span></div>
                              <span style={{ fontWeight:600, fontSize:".875rem", color:"#374151" }}>Heart Rate</span>
                              {hasLiveDevice && <span style={{ fontSize:".6rem", background:"#22c55e20", color:"#22c55e", padding:"1px 6px", borderRadius:"9999px" }}>LIVE</span>}
                            </div>
                            <div>
                              <p style={{ fontSize:"2rem", fontWeight:700, color:"#111827", margin:"0 0 .375rem" }}>
                                {deviceVitals ? fmtN(deviceVitals.hr) : (latestMeas(selPat.patient_id) ? fmtN(latestMeas(selPat.patient_id).hr) : "—")}
                                <span style={{ fontSize:"1rem", color:"#9ca3af", fontWeight:400, marginLeft:".25rem" }}>bpm</span>
                              </p>
                              {deviceVitals && <span style={{ background:SS[vStat("hr",deviceVitals.hr)].bg, color:SS[vStat("hr",deviceVitals.hr)].c, fontSize:".7rem", fontWeight:700, padding:"2px 8px", borderRadius:"9999px" }}>{vStat("hr",deviceVitals.hr)}</span>}
                            </div>
                            <div style={{ overflowX:"auto" }}><Spark data={histFor(selPat.patient_id,"hr")} color={VC.hr.color} w={160} h={36}/></div>
                          </div>
                          <div className="card" style={{ padding:"1.25rem", minHeight:160, display:"flex", flexDirection:"column", justifyContent:"space-between" }}>
                            <div style={{ display:"flex", alignItems:"center", gap:".5rem", flexWrap:"wrap" }}>
                              <div style={{ padding:".375rem", background:VC.spo2.bg, borderRadius:"50%", flexShrink:0 }}><span className="ms" style={{ color:VC.spo2.color, fontSize:".875rem" }}>air</span></div>
                              <span style={{ fontWeight:600, fontSize:".875rem", color:"#374151" }}>SpO₂</span>
                              {hasLiveDevice && <span style={{ fontSize:".6rem", background:"#22c55e20", color:"#22c55e", padding:"1px 6px", borderRadius:"9999px" }}>LIVE</span>}
                            </div>
                            <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"space-between", flexWrap:"wrap", gap:".5rem" }}>
                              <p style={{ fontSize:"2rem", fontWeight:700, color:"#111827", margin:0 }}>
                                {deviceVitals ? fmtN(deviceVitals.spo2) : (latestMeas(selPat.patient_id) ? fmtN(latestMeas(selPat.patient_id).spo2) : "—")}
                                <span style={{ fontSize:"1rem", color:"#9ca3af", fontWeight:400, marginLeft:".25rem" }}>%</span>
                              </p>
                              {deviceVitals && deviceVitals.spo2 && <Gauge value={+deviceVitals.spo2} min={85} max={100} color={VC.spo2.color}/>}
                            </div>
                            <div style={{ width:"100%", height:6, background:"#f3f4f6", borderRadius:"9999px" }}>
                              <div style={{ width:`${deviceVitals&&deviceVitals.spo2?Math.min(100,((+deviceVitals.spo2-85)/15)*100):(latestMeas(selPat.patient_id)?.spo2?Math.min(100,((+latestMeas(selPat.patient_id).spo2-85)/15)*100):0)}%`, height:"100%", background:VC.spo2.color, borderRadius:"9999px" }}/>
                            </div>
                          </div>
                        </div>
                        <Histogram userId={userId} patientId={selPat.patient_id}/>
                      </>);
                    })()}
                  </div>

                  {/* Alerts sidebar */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <h2 style={{ fontSize: "1.125rem", fontWeight: 700, color: "#111827", margin: 0 }}>Alerts <span style={{ color: "#9ca3af", fontWeight: 400 }}>({alerts.length})</span></h2>
                      <button onClick={()=>setPage("alerts")} style={{ background:"none", border:"none", color:"#6b7280", fontWeight:600, fontSize:".875rem", cursor:"pointer" }}>See All</button>
                    </div>
                    {alerts.slice(0, 5).map(a => {
                      const as2 = AS[a.status] ?? { bg:"#f3f4f6", c:"#6b7280", label:a.status };
                      const cfg = VC[a.type];
                      return (
                        <div key={a.id} className="card" style={{ padding: "1rem" }}>
                          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:".5rem", flexWrap:"wrap", gap:".5rem" }}>
                            <span style={{ fontWeight:700, fontSize:".875rem", color:"#111827" }}>{a.patient_name}</span>
                            <span style={{ background:as2.bg, color:as2.c, fontSize:".7rem", fontWeight:700, padding:"2px 8px", borderRadius:"9999px" }}>{as2.label}</span>
                          </div>
                          <p style={{ fontSize:".8125rem", color:"#6b7280", margin:"0 0 .5rem" }}>
                            {cfg?.label??a.type}: <strong style={{ color:"#111827" }}>
                              {a.type==="air_quality"?(+a.value).toFixed(0):fmtN(a.value)} {cfg?.unit??""}
                            </strong>
                          </p>
                          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                            <p style={{ fontSize:".75rem", color:"#9ca3af", margin:0 }}>{fmtT(a.created_at)}</p>
                            {a.status !== "treated" && (
                              <button onClick={()=>setTreatModal({patientId:String(a.patient_id),alertId:String(a.id)})} className={`treat-btn ${a.status==="new"?"treat-btn-urgent":""}`} style={{ fontSize:".7rem", padding:".3rem .75rem" }}>
                                <span className="ms" style={{ fontSize:".8rem" }}>medical_services</span>Treat
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {alerts.length === 0 && (
                      <div className="card" style={{ padding:"2rem", textAlign:"center" }}>
                        <span className="ms" style={{ fontSize:"2rem", color:"#e5e7eb", display:"block", marginBottom:".5rem" }}>notifications_none</span>
                        <p style={{ color:"#9ca3af", fontSize:".875rem", margin:0 }}>No alerts</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ═══════════ ALERTS ═══════════ */}
            {page === "alerts" && (
              <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
                <div><h1 style={{ fontSize:"1.875rem", fontWeight:700, color:"#111827", margin:0 }}>Alerts</h1><p style={{ color:"#6b7280", margin:".25rem 0 0", fontSize: ".875rem" }}>Click <strong>Treat</strong> on any alert to write a linked clinical observation.</p></div>
                <div style={{ display:"flex", gap:".625rem", flexWrap:"wrap", borderBottom:"1px solid #e5e7eb", paddingBottom:"1rem" }}>
                  {[["all","All"],["new","New"],["seen","Seen"],["treated","Treated"]].map(([val,label])=>(
                    <button key={val} onClick={()=>setAlertFilter(val)} className={`pill ${alertFilter===val?"pact":"pinact"}`} style={{ background:alertFilter===val?"#3b82f6":"#fff", boxShadow:"0 2px 8px rgba(0,0,0,.07)" }}>
                      {label} ({val==="all"?alerts.length:alerts.filter(a=>a.status===val).length})
                    </button>
                  ))}
                </div>
                {(alertFilter==="all"?alerts:alerts.filter(a=>a.status===alertFilter)).length===0?(
                  <div className="card" style={{ padding:"5rem", textAlign:"center" }}><span className="ms" style={{ fontSize:"3rem", color:"#e5e7eb", display:"block", marginBottom:".75rem" }}>notifications_none</span><p style={{ color:"#9ca3af" }}>No alerts in this category.</p></div>
                ):(alertFilter==="all"?alerts:alerts.filter(a=>a.status===alertFilter)).map(a=>{
                  const as2=AS[a.status]??{bg:"#f3f4f6",c:"#6b7280",label:a.status};const cfg=VC[a.type];const st=vStat(a.type,a.value);const ss=SS[st];
                  return(
                    <div key={a.id} className="card" style={{ padding:"1.5rem", borderLeft:`4px solid ${a.status==="new"?"#ef4444":a.status==="seen"?"#f59e0b":"#22c55e"}` }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:"1rem" }}>
                        <div style={{ display:"flex", alignItems:"flex-start", gap:"1rem" }}>
                          <div style={{ width:44, height:44, borderRadius:"14px", background:cfg?.bg??"#f3f4f6", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}><span className="ms" style={{ color:cfg?.color??"#6b7280", fontSize:"1.25rem" }}>{cfg?.icon??"warning"}</span></div>
                          <div>
                            <div style={{ display:"flex", alignItems:"center", gap:".625rem", flexWrap:"wrap", marginBottom:".25rem" }}>
                              <span style={{ fontWeight:700, fontSize:"1rem", color:"#111827" }}>{a.patient_name}</span>
                              <span style={{ background:as2.bg, color:as2.c, fontSize:".75rem", fontWeight:700, padding:"2px 10px", borderRadius:"9999px", display:"inline-flex", alignItems:"center", gap:".3rem" }}>{a.status==="new"&&<span className="pls" style={{ width:6, height:6, borderRadius:"50%", background:"#ef4444" }}/>}{as2.label}</span>
                              <span style={{ background:ss.bg, color:ss.c, fontSize:".75rem", fontWeight:700, padding:"2px 10px", borderRadius:"9999px" }}>{st}</span>
                            </div>
                            <p style={{ fontSize:".9375rem", color:"#374151", margin:"0 0 .25rem" }}>
                              <strong>{cfg?.label??a.type}:</strong> {a.type==="air_quality"?(+a.value).toFixed(0):fmtN(a.value)} {cfg?.unit??""}
                              {a.type==="air_quality"&&<span style={{ color:"#6b7280", fontSize:".85rem" }}> — {getAirQualityLabel(a.value)}</span>}
                            </p>
                            <p style={{ fontSize:".8125rem", color:"#9ca3af", margin:0 }}>{fmtT(a.created_at)}</p>
                            {a.comment&&<p style={{ fontSize:".875rem", color:"#374151", margin:".5rem 0 0", fontStyle:"italic", background:"#f9fafb", padding:".5rem .875rem", borderRadius:".75rem" }}>💬 {a.comment}</p>}
                          </div>
                        </div>
                        <div style={{ display:"flex", gap:".5rem", flexWrap:"wrap" }}>
                          {a.status==="new"&&<button onClick={()=>updateAlert(a.id,"seen")} style={{ padding:".5rem 1rem", background:"#fffbeb", color:"#b45309", border:"1px solid #fde68a", borderRadius:".875rem", fontWeight:700, fontSize:".8125rem", cursor:"pointer", fontFamily:"inherit", display:"inline-flex", alignItems:"center", gap:".3rem" }}><span className="ms" style={{ fontSize:".875rem" }}>visibility</span>Mark Seen</button>}
                          {/* Always show Treat/Observe button regardless of status */}
                          <button onClick={()=>setTreatModal({patientId:String(a.patient_id),alertId:String(a.id)})} className={`treat-btn ${a.status==="new"?"treat-btn-urgent":""}`}>
                            <span className="ms" style={{ fontSize:".875rem" }}>medical_services</span>
                            {a.status==="treated" ? "Observe" : "Treat"}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ═══════════ VITAL SIGNS ═══════════ */}
            {page === "vitals" && (
              <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
                <div><h1 style={{ fontSize:"1.875rem", fontWeight:700, color:"#111827", margin:0 }}>Vital Signs</h1><p style={{ color:"#6b7280", margin:".25rem 0 0", fontSize: ".875rem" }}>Full history, trends and status per patient.</p></div>
                {patients.map(p => {
                  const deviceVitals  = getMergedVitals(p.patient_id);
                  const latest        = deviceVitals || latestMeas(p.patient_id);
                  const st            = patStatus(p.patient_id);
                  const ss            = SS[st];
                  const hasLiveDevice = deviceVitals !== null;
                  return (
                    <div key={p.patient_id} className="card" style={{ padding: "1.5rem" }}>
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"1.25rem", flexWrap:"wrap", gap:".75rem" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:".875rem" }}>
                          <div style={{ width:48, height:48, borderRadius:"14px", background:"#3b82f6", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:700, fontSize:"1.25rem", boxShadow:"0 4px 12px rgba(59,130,246,.35)", flexShrink:0 }}>{p.name.charAt(0)}</div>
                          <div>
                            <h3 style={{ fontWeight:700, color:"#111827", margin:0, fontSize:"1.125rem" }}>{p.name}</h3>
                            <p style={{ fontSize:".8125rem", color:"#9ca3af", margin:0 }}>{hasLiveDevice ? "📡 Device live" : (latest ? `Last: ${fmtT(latest.created_at)}` : "No data yet")}</p>
                          </div>
                        </div>
                        <span style={{ background:ss.bg, color:ss.c, fontSize:".875rem", fontWeight:700, padding:"6px 16px", borderRadius:"9999px", display:"flex", alignItems:"center", gap:".5rem" }}>
                          <span style={{ width:8, height:8, borderRadius:"50%", background:ss.dot }}/>{st}
                          {hasLiveDevice && <span style={{ fontSize:".65rem", background:"#22c55e20", color:"#22c55e", padding:"1px 5px", borderRadius:"9999px", marginLeft:".25rem" }}>LIVE</span>}
                        </span>
                      </div>
                      <div className="vit-grid" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))", gap:"1rem", marginBottom:"1.5rem" }}>
                        {Object.entries(VC).map(([col, cfg]) => {
                          const val = latest ? latest[col] : null;
                          const vst = vStat(col, val);
                          const vss = SS[vst];
                          const history = histFor(p.patient_id, col);
                          return (
                            <div key={col} style={{ borderRadius:"1rem", padding:"1rem", background:"#f9fafb", border:`1px solid ${vst==="Abnormal"?"#fecaca":vst==="Watch"?"#fde68a":"#f3f4f6"}` }}>
                              <div style={{ display:"flex", alignItems:"center", gap:".5rem", marginBottom:".625rem" }}>
                                <div style={{ width:32, height:32, borderRadius:"10px", background:cfg.bg, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                                  <span className="ms" style={{ color:cfg.color, fontSize:".9rem" }}>{cfg.icon}</span>
                                </div>
                                <div style={{ flex:1, minWidth:0 }}>
                                  <p style={{ fontSize:".75rem", fontWeight:700, color:"#374151", margin:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{cfg.label}</p>
                                  <span style={{ background:vss.bg, color:vss.c, fontSize:".6rem", fontWeight:700, padding:"1px 6px", borderRadius:"9999px" }}>{vst}</span>
                                </div>
                              </div>
                              <p style={{ fontSize:"1.5rem", fontWeight:800, color:"#111827", margin:"0 0 .25rem", lineHeight:1 }}>
                                {val != null ? (col === "air_quality" ? (+val).toFixed(0) : fmtN(val)) : "—"}
                                <span style={{ fontSize:".8rem", color:"#9ca3af", fontWeight:400, marginLeft:".25rem" }}>{cfg.unit}</span>
                              </p>
                              {col === "air_quality" && val != null && <p style={{ fontSize:".7rem", color:"#6b7280", margin:"0 0 .5rem", fontWeight:600 }}>{getAirQualityLabel(val)}</p>}
                              {history.length > 1 && <div style={{ marginBottom:".375rem" }}><Spark data={history} color={cfg.color} w={150} h={28} animate={false}/></div>}
                            </div>
                          );
                        })}
                      </div>
                      {measFor(p.patient_id).length > 0 && (
                        <div>
                          <h4 style={{ fontWeight:700, color:"#374151", fontSize:".9375rem", margin:"0 0 .75rem" }}>Measurement History</h4>
                          <div style={{ overflowX:"auto", maxHeight:240, overflowY:"auto" }}>
                            <table style={{ width:"100%", fontSize:".8125rem", borderCollapse:"collapse", minWidth:500 }}>
                              <thead>
                                <tr style={{ borderBottom:"2px solid #f3f4f6" }}>
                                  <th style={{ textAlign:"left", padding:".5rem .75rem .5rem 0", fontSize:".7rem", textTransform:"uppercase", letterSpacing:".05em", color:"#9ca3af", fontWeight:700 }}>Date</th>
                                  {TABLE_VITALS.map(h => <th key={h.k} style={{ textAlign:"left", padding:".5rem .75rem .5rem 0", fontSize:".7rem", textTransform:"uppercase", letterSpacing:".05em", color:"#9ca3af", fontWeight:700 }}>{h.l}</th>)}
                                </tr>
                              </thead>
                              <tbody>
                                {measFor(p.patient_id).sort((a,b)=>new Date(b.created_at)-new Date(a.created_at)).slice(0,20).map(row => (
                                  <tr key={row.id} className="trow" style={{ borderBottom:"1px solid #f9fafb" }}>
                                    <td style={{ padding:".5rem .75rem .5rem 0", color:"#6b7280", whiteSpace:"nowrap" }}>{fmtT(row.created_at)}</td>
                                    {TABLE_VITALS.map(col => {
                                      const vst = vStat(col.k, row[col.k]);
                                      const displayVal = row[col.k] != null ? (col.k === "air_quality" ? (+row[col.k]).toFixed(0) : fmtN(row[col.k])) : "—";
                                      return (
                                        <td key={col.k} style={{ padding:".5rem .75rem .5rem 0" }}>
                                          <span style={{ fontWeight:600, color:vst==="Abnormal"?"#ef4444":vst==="Watch"?"#b45309":"#111827" }}>{displayVal}</span>
                                          {col.k === "air_quality" && row[col.k] != null && <span style={{ fontSize:".65rem", color:"#9ca3af", marginLeft:".25rem" }}>({getAirQualityLabel(row[col.k])})</span>}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* ═══════════ CALENDAR ═══════════ */}
            {page === "calendar" && (
              <div className="cal-grid" style={{ display:"grid", gridTemplateColumns:"1fr 320px", gap:"1.5rem" }}>
                <div style={{ display:"flex", flexDirection:"column", gap:"1.5rem" }}>
                  <div><h1 style={{ fontSize:"1.875rem", fontWeight:700, color:"#111827", margin:0 }}>Device Usage Calendar</h1><p style={{ color:"#6b7280", margin:".25rem 0 0", fontSize: ".875rem" }}>Blue = recorded measurement. Red = no data.</p></div>
                  <div style={{ display:"flex", gap:".5rem", flexWrap:"wrap" }}>
                    {patients.map(p=><button key={p.patient_id} onClick={()=>setCalPat(p)} className={`pill ${calPat?.patient_id===p.patient_id?"pact":"pinact"}`} style={{ background:calPat?.patient_id===p.patient_id?"#3b82f6":"#fff", boxShadow:"0 2px 8px rgba(0,0,0,.07)" }}>{p.name}</button>)}
                  </div>
                  <div className="card" style={{ padding:"1.5rem" }}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"1.5rem" }}>
                      <h2 style={{ fontWeight:700, fontSize:"1.25rem", color:"#111827", margin:0 }}>{MONTHS[calMonth]} {calYear}</h2>
                      <div style={{ display:"flex", gap:".5rem" }}>
                        <button onClick={()=>{if(calMonth===0){setCalMonth(11);setCalYear(y=>y-1);}else setCalMonth(m=>m-1);}} style={{ width:36, height:36, borderRadius:"50%", border:"1px solid #e5e7eb", background:"#fff", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#6b7280" }}><span className="ms" style={{ fontSize:"1.125rem" }}>chevron_left</span></button>
                        <button onClick={()=>{if(calMonth===11){setCalMonth(0);setCalYear(y=>y+1);}else setCalMonth(m=>m+1);}} style={{ width:36, height:36, borderRadius:"50%", border:"1px solid #e5e7eb", background:"#fff", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#6b7280" }}><span className="ms" style={{ fontSize:"1.125rem" }}>chevron_right</span></button>
                      </div>
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", textAlign:"center", marginBottom:".75rem" }}>{DAYS.map(d=><div key={d} style={{ fontSize:".75rem", fontWeight:700, color:"#9ca3af" }}>{d}</div>)}</div>
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", rowGap:".5rem" }}>
                      {Array.from({length:fdm(calYear,calMonth)}).map((_,i)=><div key={`e${i}`}/>)}
                      {Array.from({length:dim(calYear,calMonth)}).map((_,i)=>{
                        const day=i+1;const future=new Date(calYear,calMonth,day)>new Date();const used=isDev(day);let cls="calday";
                        if(future)cls+=" cdfut";else if(used)cls+=" cdused";else cls+=" cdmiss";
                        return<div key={day} style={{ textAlign:"center" }}><div className={cls}>{day}</div></div>;
                      })}
                    </div>
                    <div style={{ display:"flex", gap:"1.5rem", marginTop:"1.5rem", flexWrap:"wrap" }}>
                      {[{c:"#3b82f6",l:"Device Used"},{c:"#ef4444",l:"No Data"},{c:"#d1d5db",l:"Future"}].map(x=>(
                        <div key={x.l} style={{ display:"flex", alignItems:"center", gap:".5rem" }}><div style={{ width:12, height:12, borderRadius:"50%", background:x.c }}/><span style={{ fontSize:".8125rem", color:"#6b7280", fontWeight:500 }}>{x.l}</span></div>
                      ))}
                    </div>
                  </div>
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:"1.5rem" }}>
                  <div className="card" style={{ padding:"1.5rem" }}>
                    <h3 style={{ fontWeight:700, color:"#111827", margin:"0 0 1rem", fontSize:"1rem" }}>Month Summary</h3>
                    {calPat&&(()=>{
                      const total=dim(calYear,calMonth);const now2=new Date();
                      const passed=calYear<now2.getFullYear()||(calYear===now2.getFullYear()&&calMonth<now2.getMonth())?total:calYear===now2.getFullYear()&&calMonth===now2.getMonth()?now2.getDate():0;
                      const used2=deviceDays.length;const missed=passed-used2;const pct=passed>0?Math.round((used2/passed)*100):0;
                      return(<>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:".75rem", marginBottom:"1rem" }}>
                          {[{l:"Days Used",v:used2,c:"#3b82f6",bg:"#eff6ff"},{l:"Missed",v:missed,c:"#ef4444",bg:"#fef2f2"},{l:"Total Days",v:passed,c:"#6b7280",bg:"#f3f4f6"},{l:"Compliance",v:`${pct}%`,c:pct>=80?"#16a34a":pct>=50?"#b45309":"#991b1b",bg:pct>=80?"#f0fdf4":pct>=50?"#fffbeb":"#fef2f2"}].map(s=>(
                            <div key={s.l} style={{ borderRadius:"1rem", padding:".875rem", background:s.bg, textAlign:"center" }}><p style={{ fontSize:"1.5rem", fontWeight:700, color:s.c, margin:0 }}>{s.v}</p><p style={{ fontSize:".75rem", color:"#6b7280", margin:".25rem 0 0" }}>{s.l}</p></div>
                          ))}
                        </div>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:".375rem" }}><span style={{ fontSize:".8125rem", fontWeight:600, color:"#374151" }}>Compliance Rate</span><span style={{ fontSize:".8125rem", fontWeight:700, color:pct>=80?"#16a34a":pct>=50?"#b45309":"#991b1b" }}>{pct}%</span></div>
                        <div style={{ height:8, background:"#f3f4f6", borderRadius:"9999px" }}><div style={{ width:`${pct}%`, height:"100%", borderRadius:"9999px", background:pct>=80?"#22c55e":pct>=50?"#f59e0b":"#ef4444", transition:"width .5s" }}/></div>
                      </>);
                    })()}
                  </div>
                </div>
              </div>
            )}

            {/* ═══════════ OBSERVATIONS ═══════════ */}
            {page === "observations" && (
              <div className="obs-grid" style={{ display:"grid", gridTemplateColumns:"1fr 360px", gap:"1.5rem" }}>
                <div style={{ display:"flex", flexDirection:"column", gap:"1.5rem" }}>
                  <div><h1 style={{ fontSize:"1.875rem", fontWeight:700, color:"#111827", margin:0 }}>Observations</h1><p style={{ color:"#6b7280", margin:".25rem 0 0", fontSize: ".875rem" }}>Every note is emailed to the patient with their current vitals.</p></div>
                  {observations.length===0?(
                    <div className="card" style={{ padding:"5rem", textAlign:"center" }}><span className="ms" style={{ fontSize:"3rem", color:"#e5e7eb", display:"block", marginBottom:".75rem" }}>edit_note</span><p style={{ color:"#9ca3af" }}>No observations yet.</p></div>
                  ):observations.map(o=>{
                    const isThreat=o.is_threat;const emailSent=o.email_sent;const vitals=o.vitals_snapshot?(typeof o.vitals_snapshot==="string"?JSON.parse(o.vitals_snapshot):o.vitals_snapshot):null;const hasAlert=!!o.alert_id;
                    return(
                      <div key={o.id} className="card hovcard" style={{ padding:"1.25rem", display:"flex", alignItems:"flex-start", gap:"1rem", borderLeft:`4px solid ${isThreat?"#ef4444":hasAlert?"#22c55e":"#3b82f6"}` }}>
                        <div style={{ width:44, height:44, borderRadius:"14px", background:isThreat?"#fef2f2":"#eff6ff", color:isThreat?"#ef4444":"#3b82f6", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700, fontSize:"1.125rem", flexShrink:0 }}>{(o.patient_name||"?").charAt(0)}</div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:".5rem", marginBottom:".375rem", flexWrap:"wrap" }}>
                            <span style={{ fontWeight:700, color:"#111827" }}>{o.patient_name||"—"}</span>
                            {isThreat&&<span style={{ background:"#fef2f2", color:"#991b1b", fontSize:".65rem", fontWeight:700, padding:"2px 8px", borderRadius:"9999px" }}>⚠️ THREAT</span>}
                            {hasAlert&&<span style={{ background:"#f0fdf4", color:"#166534", fontSize:".65rem", fontWeight:700, padding:"2px 8px", borderRadius:"9999px", display:"inline-flex", alignItems:"center", gap:".25rem" }}><span className="ms" style={{ fontSize:".7rem" }}>medical_services</span>Alert #{o.alert_id}</span>}
                            {emailSent?<span style={{ background:"#f0fdf4", color:"#166534", fontSize:".65rem", fontWeight:700, padding:"2px 8px", borderRadius:"9999px" }}>✓ Email Sent</span>:<span style={{ background:"#fffbeb", color:"#b45309", fontSize:".65rem", fontWeight:700, padding:"2px 8px", borderRadius:"9999px" }}>Email Pending</span>}
                            <span style={{ fontSize:".75rem", color:"#9ca3af", marginLeft:"auto" }}>{fmtT(o.created_at)}</span>
                          </div>
                          <p style={{ color:"#374151", fontSize:".9375rem", margin:"0 0 .5rem", lineHeight:1.6 }}>{o.note}</p>
                          {vitals&&(
                            <div style={{ display:"flex", gap:".4rem", flexWrap:"wrap", marginTop:".375rem" }}>
                              {[{k:"hr",l:"HR",u:"bpm",c:"#ef4444"},{k:"spo2",l:"SpO₂",u:"%",c:"#3b82f6"},{k:"temp_body",l:"Temp",u:"°C",c:"#f59e0b"},{k:"air_quality",l:"Air",u:"ppm",c:"#10b981"}].filter(m=>vitals[m.k]!=null).map(m=>(
                                <span key={m.k} style={{ background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:".5rem", padding:".2rem .6rem", fontSize:".7rem", fontWeight:700, color:m.c }}>
                                  {m.l}: {m.k==="air_quality"?(+vitals[m.k]).toFixed(0):(+vitals[m.k]).toFixed(1)}<span style={{ color:"#9ca3af", fontWeight:400 }}>{m.u}</span>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div style={{ display:"flex", gap:".375rem", flexShrink:0 }}>
                          <button onClick={()=>setViewObs(o)} style={{ width:32, height:32, borderRadius:"10px", background:"#eff6ff", border:"none", color:"#3b82f6", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}><span className="ms" style={{ fontSize:".9rem" }}>open_in_new</span></button>
                          <button onClick={()=>deleteObs(o.id)} style={{ width:32, height:32, borderRadius:"10px", background:"#fef2f2", border:"none", color:"#ef4444", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}><span className="ms" style={{ fontSize:".9rem" }}>delete</span></button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div style={{ position:"sticky", top:"5rem" }}>
                  <div className="card" style={{ padding:"1.5rem", marginBottom:"1rem" }}>
                    <button onClick={()=>setTreatModal({})} style={{ width:"100%", padding:"1rem", background:"linear-gradient(135deg,#22c55e,#16a34a)", color:"#fff", border:"none", borderRadius:"1rem", fontWeight:700, fontSize:".9375rem", cursor:"pointer", fontFamily:"inherit", display:"flex", alignItems:"center", justifyContent:"center", gap:".625rem", boxShadow:"0 4px 16px rgba(34,197,94,.35)", marginBottom:".875rem" }}>
                      <span className="ms" style={{ fontSize:"1.125rem" }}>medical_services</span>Treat an Alert
                    </button>
                    <p style={{ fontSize:".75rem", color:"#9ca3af", textAlign:"center", margin:0 }}>Link an observation to an alert with exact historical vitals</p>
                  </div>
                  <div className="card" style={{ padding:"1.5rem" }}>
                    <h3 style={{ fontWeight:700, fontSize:"1rem", color:"#111827", margin:"0 0 1.25rem" }}>Quick Observation</h3>
                    <div style={{ display:"flex", flexDirection:"column", gap:".875rem" }}>
                      <div><label style={{ display:"block", fontSize:".7rem", fontWeight:700, color:"#9ca3af", textTransform:"uppercase", letterSpacing:".05em", marginBottom:".375rem" }}>Patient</label><select className="inp" value={obsPatId} onChange={e=>setObsPatId(e.target.value)}><option value="">Select patient…</option>{patients.map(p=><option key={p.patient_id} value={p.patient_id}>{p.name}</option>)}</select></div>
                      <div>
                        <label style={{ display:"block", fontSize:".7rem", fontWeight:700, color:"#9ca3af", textTransform:"uppercase", letterSpacing:".05em", marginBottom:".375rem" }}>Note</label>
                        <textarea className="inp" rows={5} style={{ resize:"none", lineHeight:1.6 }} placeholder="Write a clinical observation…" value={obsText} onChange={e=>setObsText(e.target.value)}/>
                        {obsText&&obsIsThreat&&(<div style={{ marginTop:".5rem", background:"#fef2f2", border:"1px solid #fecaca", borderRadius:".75rem", padding:".5rem .875rem", display:"flex", alignItems:"center", gap:".5rem" }}><span className="ms" style={{ color:"#ef4444", fontSize:".875rem" }}>warning</span><span style={{ fontSize:".75rem", fontWeight:700, color:"#991b1b" }}>⚠️ Threat keywords detected</span></div>)}
                      </div>
                      <button onClick={addObs} disabled={emailSending} className="bblu" style={{ justifyContent:"center", width:"100%", boxShadow:"0 4px 14px rgba(59,130,246,.3)" }}>
                        {emailSending?<><div className="spin" style={{ width:16, height:16, border:"2px solid rgba(255,255,255,.4)", borderTopColor:"#fff", borderRadius:"50%" }}/>Sending…</>:<><span className="ms" style={{ fontSize:"1.125rem" }}>send</span>Save &amp; Send Email</>}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ═══════════ AI CHATBOT ═══════════ */}
            {page === "chatbot" && (
              <div>
                <div style={{ marginBottom:"1.5rem" }}><h1 style={{ fontSize:"1.875rem", fontWeight:700, color:"#111827", margin:0 }}>AI Health Assistant</h1><p style={{ color:"#6b7280", margin:".25rem 0 0", fontSize: ".875rem" }}>Medical AI — predicts vitals, detects diseases, analyzes trends.</p></div>
                <AIChatbot patients={patients} measurements={measurements} alerts={alerts} userId={userId}/>
              </div>
            )}

            {/* ═══════════ HOSPITAL CHAT ═══════════ */}
            {page === "chat" && (
              <div>
                <div style={{ marginBottom:"1.5rem" }}><h1 style={{ fontSize:"1.875rem", fontWeight:700, color:"#111827", margin:0 }}>Hospital Chat</h1><p style={{ color:"#6b7280", margin:".25rem 0 0", fontSize: ".875rem" }}>Private messaging with all hospital staff.</p></div>
                {me.hospital_id?<HospitalChat userId={userId} hospitalId={me.hospital_id}/>:<div className="card" style={{ padding:"4rem", textAlign:"center" }}><p style={{ color:"#9ca3af" }}>Hospital information not available.</p></div>}
              </div>
            )}

          </>)}
        </div>
      </div>

      {/* ══ MOBILE BOTTOM NAV ══ */}
      <nav className="msd-bottom-nav">
        {navItems.map(n => (
          <button key={n.id} onClick={() => setPage(n.id)} className={`mob-nav-item ${page === n.id ? "active" : ""}`}>
            <span className="ms" style={{ fontSize: "1.375rem", lineHeight: 1 }}>{n.icon}</span>
            {n.short || n.label}
            {n.badge > 0 && (
              <span style={{ position: "absolute", top: 6, right: "50%", transform: "translateX(calc(50% + 8px))", background: n.id === "chat" ? "#8b5cf6" : "#ef4444", color: "#fff", fontSize: ".5rem", fontWeight: 800, padding: "1px 5px", borderRadius: "9999px", lineHeight: 1.5 }}>
                {n.badge}
              </span>
            )}
          </button>
        ))}
        <button onClick={logout} className="mob-nav-item" style={{ color: "#ef4444" }}>
          <span className="ms" style={{ fontSize: "1.375rem", lineHeight: 1 }}>logout</span>Out
        </button>
      </nav>

      {/* ══ VIEW OBS MODAL ══ */}
      {viewObs && (() => {
        const vitals = viewObs.vitals_snapshot ? (typeof viewObs.vitals_snapshot === "string" ? JSON.parse(viewObs.vitals_snapshot) : viewObs.vitals_snapshot) : null;
        const OBS_VITALS = [
          {k:"hr",l:"HR",u:"bpm",c:"#ef4444"},{k:"spo2",l:"SpO₂",u:"%",c:"#3b82f6"},
          {k:"temp_body",l:"Body Temp",u:"°C",c:"#f59e0b"},{k:"humidity",l:"Humidity",u:"%",c:"#06b6d4"},{k:"air_quality",l:"Air Quality",u:"ppm",c:"#10b981"},
        ];
        return (
          <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) setViewObs(null); }}>
            <div className="card modal-box slide-up" style={{ width: "100%", maxWidth: 520, padding: "2rem" }}>
              <div style={{ width: 40, height: 4, background: "#e5e7eb", borderRadius: 9999, margin: "-1rem auto 1.5rem" }} />
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"1.25rem" }}>
                <div>
                  <div style={{ display:"flex", alignItems:"center", gap:".625rem", marginBottom:".25rem" }}>
                    <h2 style={{ fontSize:"1.25rem", fontWeight:700, color:"#111827", margin:0 }}>Observation</h2>
                    {viewObs.is_threat&&<span style={{ background:"#fef2f2", color:"#991b1b", fontSize:".7rem", fontWeight:700, padding:"2px 8px", borderRadius:"9999px" }}>⚠️ THREAT</span>}
                    {viewObs.email_sent&&<span style={{ background:"#f0fdf4", color:"#166534", fontSize:".7rem", fontWeight:700, padding:"2px 8px", borderRadius:"9999px" }}>✓ Email Sent</span>}
                  </div>
                  <p style={{ fontSize:".8125rem", color:"#9ca3af", margin:0 }}>{viewObs.patient_name} · {fmtT(viewObs.created_at)}</p>
                </div>
                <button onClick={()=>setViewObs(null)} style={{ width:32, height:32, borderRadius:"50%", background:"#f3f4f6", border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#6b7280" }}>
                  <span className="ms" style={{ fontSize:"1rem" }}>close</span>
                </button>
              </div>
              <div style={{ background:"#f9fafb", borderRadius:"1rem", padding:"1.25rem", fontSize:".9375rem", color:"#374151", lineHeight:1.7, whiteSpace:"pre-wrap", marginBottom:"1rem" }}>
                {viewObs.note}
              </div>
              {vitals && (
                <div style={{ background:"#eff6ff", borderRadius:"1rem", padding:"1rem", marginBottom:"1rem" }}>
                  <p style={{ fontSize:".75rem", fontWeight:700, color:"#1e40af", margin:"0 0 .625rem", textTransform:"uppercase", letterSpacing:".05em" }}>Vitals snapshot</p>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:".5rem" }}>
                    {OBS_VITALS.filter(m=>vitals[m.k]!=null).map(m=>(
                      <div key={m.k} style={{ background:"#fff", border:"1px solid #bfdbfe", borderRadius:".75rem", padding:".5rem .875rem", textAlign:"center" }}>
                        <p style={{ fontSize:".65rem", color:"#9ca3af", margin:0, fontWeight:600, textTransform:"uppercase" }}>{m.l}</p>
                        <p style={{ fontSize:"1.125rem", fontWeight:700, color:m.c, margin:0 }}>{m.k==="air_quality"?(+vitals[m.k]).toFixed(0):(+vitals[m.k]).toFixed(1)}<span style={{ fontSize:".75rem", color:"#9ca3af", fontWeight:400 }}>{m.u}</span></p>
                        {m.k==="air_quality"&&<p style={{ fontSize:".6rem", color:"#6b7280", margin:0 }}>{getAirQualityLabel(vitals[m.k])}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div style={{ display:"flex", gap:".75rem" }}>
                <button onClick={()=>{deleteObs(viewObs.id);setViewObs(null);}} style={{ flex:1, padding:".75rem", background:"#fef2f2", color:"#ef4444", border:"none", borderRadius:".875rem", fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>Delete</button>
                <button onClick={()=>setViewObs(null)} style={{ flex:1, padding:".75rem", background:"#f3f4f6", color:"#374151", border:"none", borderRadius:".875rem", fontWeight:600, fontSize:".9375rem", cursor:"pointer", fontFamily:"inherit" }}>Close</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ══ TREAT MODAL ══ */}
      {treatModal !== null && (
        <TreatModal
          patients={patients}
          userId={userId}
          preselectedPatientId={treatModal.patientId}
          preselectedAlertId={treatModal.alertId}
          onClose={() => setTreatModal(null)}
          onSuccess={async (data) => {
            setTreatModal(null);
            alert(`Observation saved and email sent to patient.`);

            // Mark the originating notification as done if triggered from one
            if (treatModal.notifId) {
              try {
                await fetch(`${API}/user-notifications/${treatModal.notifId}/status`, {
                  method: "PUT",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ status: "done", actorId: Number(userId) }),
                });
              } catch (e) { console.error(e); }
            }

            fetchData(false);
          }}
        />
      )}
    </div>
  );
}